/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.75: eslint-plugin-react-hooks v5 introduces an experimental
 * `set-state-in-effect` rule that fires on:
 *   1. Genuine external-prop → local-state sync useEffects (`household`
 *      → local edit-state copies), which are the canonical React pattern
 *      for "make this controlled-from-server input editable".
 *   2. setState calls inside async IIFEs inside useEffects (correct
 *      "fetch on dependency change" pattern).
 *   3. EVENT HANDLERS (saveName, savePlaceholder, revokeAccess) defined
 *      inside the same component as effects — a clear false positive.
 *
 * The rule has open RFC issues acknowledging these false positives and
 * is not enabled in React 19's recommended preset. Disabling at file
 * level is the documented community workaround until the rule
 * stabilises. Other React-19 lints (rules-of-hooks, exhaustive-deps)
 * remain enabled.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { downloadFile } from "@/lib/downloadFile";
import { celebrate } from "@/lib/celebrate";
import { useAuth } from "@/context/AuthContext";
import { useChildScope } from "@/context/ChildScopeContext";
import { Button } from "@/components/ui/button";
import { SubscriptionCard } from "@/components/SubscriptionCard";
import { YourChildrenCard } from "@/components/YourChildrenCard";
import { BillingHistoryCard } from "@/components/BillingHistoryCard";
import { CalendarSyncCard } from "@/components/CalendarSyncCard";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Copy, RotateCw, ShieldCheck, ShieldOff, Download, UserCog, Trash2, Bell, BellOff, BellRing, KeyRound, Monitor, LogOut, AlertTriangle, Lightbulb, CheckCircle2, ArrowRight } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { AppearanceCard } from "@/components/AppearanceCard";
import { MobileNavCard } from "@/components/MobileNavCard";
import { subscribeToPush, unsubscribeFromPush, isPushSupported, getPushSubscription } from "@/lib/push";
import { formatDT, money as formatMoney } from "@/lib/format";
import { ARRANGEMENT_TYPES, ARRANGEMENT_LABEL } from "@/lib/parentingArrangements";
import { toast } from "sonner";

const CURRENCIES = ["EUR", "GBP", "USD", "CAD", "AUD", "CHF", "SEK", "NOK", "DKK", "NZD"];
const SPLIT_OPTIONS = [
  { value: "50_50", label: "50 / 50" },
  { value: "60_40", label: "60 / 40 (payer gets 60%)" },
  { value: "70_30", label: "70 / 30 (payer gets 70%)" },
  // v77.99.63 — Sole-parent / no-shared-time preset. Payer keeps 100%
  // of the cost so the Expenses page reads "tracking spend", not
  // "splitting with a co-parent". The onboarding wizard sets this
  // automatically when the user picks "No current shared parenting
  // time", but exposing it here lets any user opt in/out manually.
  { value: "100_0", label: "100 / 0 (payer keeps the whole cost)" },
  { value: "custom", label: "Custom payer share" },
];

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.round((Date.now() - then) / 1000);
  if (diff < 30) return "just now";
  if (diff < 90) return "1 minute ago";
  if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
  if (diff < 7200) return "1 hour ago";
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  if (diff < 172800) return "yesterday";
  if (diff < 2592000) return `${Math.floor(diff / 86400)} days ago`;
  return new Date(iso).toLocaleDateString();
}

const CORE_PERMS = [
  { key: "view_messages",  label: "View messages",                     hint: "Read all messages between parents. View only, cannot reply." },
  { key: "view_calendar",  label: "View calendar",                     hint: "See shared custody events, schedules and agreed changes." },
  { key: "view_expenses",  label: "View expenses",                     hint: "See logged expenses and totals." },
  { key: "view_upcoming_expenses", label: "View upcoming expenses",    hint: "See flagged future costs and conversion status. Granted independently of Expenses." },
  { key: "view_documents", label: "View documents",                    hint: "Access the shared document vault." },
  { key: "view_journal",   label: "View shared journal entries",       hint: "Entries that both parents have chosen to share." },
  { key: "view_requests",  label: "View requests and amendments",      hint: "Full history of requests, agreements and not-agreed outcomes." },
];
const OPTIONAL_PERMS = [
  { key: "view_private_journal",    label: "View private journal entries", hint: "Sensitive — includes entries not shared with the other parent." },
  { key: "view_private_documents",  label: "View private documents",       hint: "Sensitive — includes documents not shared with the other parent." },
  { key: "view_expense_details",    label: "View full expense logs",       hint: "Detailed breakdowns: who paid what, when and how." },
];

const PermRow = ({ perm, value, onChange, testid }) => (
  <label className="flex items-start justify-between gap-3 py-3 border-b border-[#D9E1E3] last:border-0 cursor-pointer select-none">
    <div className="min-w-0">
      <div className="text-sm text-[#2E3A3F] font-medium">{perm.label}</div>
      <div className="text-xs text-[#6F828A] mt-0.5">{perm.hint}</div>
    </div>
    <Switch checked={!!value} onCheckedChange={onChange} data-testid={testid} />
  </label>
);

export default function Settings() {
  const { user, logout, household, members, currency, loadHousehold, refresh } = useAuth();
  // v77.49 — Honour a `#third-party` (or any other section id) hash on
  // mount so the menu's "Third-party access" link scrolls the user
  // straight to that section. Small timeout lets the page render first.
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash) {
      const id = window.location.hash.replace("#", "");
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
      return () => clearTimeout(t);
    }
  }, []);
  const [audit, setAudit] = useState([]);
  const [bundleDays, setBundleDays] = useState(30);
  const [cur, setCur] = useState(currency);
  const [defaultSplit, setDefaultSplit] = useState({ split_type: "50_50", split_payer_share: 50 });
  // v77.99.9 — Household-default parenting arrangement. Empty string =
  // "Auto" (no household-wide override; per-child or data-derived).
  const [defaultArrangement, setDefaultArrangement] = useState("");

  // Mediator invites
  const [mediatorInvites, setMediatorInvites] = useState([]);
  const [mediatorDays, setMediatorDays] = useState(30);
  const [mediatorNote, setMediatorNote] = useState("");
  const [mediatorPerms, setMediatorPerms] = useState({
    view_messages: true, view_calendar: true, view_expenses: true,
    view_documents: true, view_journal: true, view_requests: true,
    view_private_journal: false, view_private_documents: false, view_expense_details: false,
  });
  // v77.48 — Per-child scope for the mediator/solicitor invite. The
  // granter must explicitly select which children this third party
  // can see; empty list = no per-child data visible. Defaults to
  // every child the granter has access to so the common case (one
  // family, one solicitor) stays one-click.
  const [mediatorChildIds, setMediatorChildIds] = useState([]);
  const [mediatorChildren, setMediatorChildren] = useState([]);
  // v77.99.150 (iter 122.loading-states) — Prevents "No children
  // available to share." rendering before the first /children fetch.
  const [mediatorChildrenLoaded, setMediatorChildrenLoaded] = useState(false);
  const [editScopeFor, setEditScopeFor] = useState(null);
  const [editScopeIds, setEditScopeIds] = useState([]);
  // v77.49 — Activity-log dialog state. Loaded lazily on open.
  const [viewsFor, setViewsFor] = useState(null);
  const [views, setViews] = useState([]);
  const [viewsLoading, setViewsLoading] = useState(false);
  const [editPermsFor, setEditPermsFor] = useState(null);
  const [editPerms, setEditPerms] = useState({});

  // 2FA
  const [totpSetup, setTotpSetup] = useState(null);
  const [totpCode, setTotpCode] = useState("");
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  // v77.99.117 — Recovery codes are shown exactly once after enable
  // or regenerate. Persisting them in localStorage would defeat the
  // purpose; the user must save them at that moment.
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenCode, setRegenCode] = useState("");
  const [regenBusy, setRegenBusy] = useState(false);

  // Change password
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState("");

  // Active sessions
  const [sessions, setSessions] = useState([]);
  const [currentJti, setCurrentJti] = useState(null);
  const [sessionsBusy, setSessionsBusy] = useState(false);

  // Security activity
  const [securityActivity, setSecurityActivity] = useState([]);

  // Account deletion
  const [delOpen, setDelOpen] = useState(false);
  const [delAck, setDelAck] = useState(false);
  const [delPassword, setDelPassword] = useState("");
  const [delPhrase, setDelPhrase] = useState("");
  const [delBusy, setDelBusy] = useState(false);
  const [delError, setDelError] = useState("");

  // Display-name editing
  const [nameDraft, setNameDraft] = useState("");
  const [nameBusy, setNameBusy] = useState(false);

  // Unlink co-parent
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const [unlinkPreflight, setUnlinkPreflight] = useState(null);
  const [unlinkAck, setUnlinkAck] = useState(false);
  const [unlinkConfirmName, setUnlinkConfirmName] = useState("");
  const [unlinkBusy, setUnlinkBusy] = useState(false);
  const [unlinkError, setUnlinkError] = useState("");

  // Notifications prefs / pause
  const [prefs, setPrefs] = useState(null);
  const [pausedUntil, setPausedUntil] = useState(null);
  const [pushState, setPushState] = useState({ supported: false, subscribed: false });

  // Feature requests
  const [frOpen, setFrOpen] = useState(false);
  const [frTitle, setFrTitle] = useState("");
  const [frDesc, setFrDesc] = useState("");
  const [frCategory, setFrCategory] = useState("");
  const [frPriority, setFrPriority] = useState("");
  const [frBusy, setFrBusy] = useState(false);
  const [frError, setFrError] = useState("");
  const [frSubmitted, setFrSubmitted] = useState(false);
  const [frMine, setFrMine] = useState([]);
  const [frShowMine, setFrShowMine] = useState(false);

  const load = useCallback(async () => {
    const [a, mi, np, ss, sa, ch] = await Promise.all([
      api.get("/audit"),
      api.get("/mediator/invites").catch(() => ({ data: [] })),
      api.get("/notifications/prefs").catch(() => ({ data: null })),
      api.get("/auth/sessions").catch(() => ({ data: { sessions: [], current_jti: null } })),
      api.get("/auth/activity?limit=8").catch(() => ({ data: { events: [] } })),
      // v77.48 — pre-load children so the mediator scope picker can
      // render without a second roundtrip when the user opens the
      // create form.
      api.get("/children").catch(() => ({ data: [] })),
    ]);
    setAudit(a.data);
    setMediatorInvites(mi.data || []);
    if (np.data) { setPrefs(np.data.prefs); setPausedUntil(np.data.paused_until); }
    setSessions(ss.data?.sessions || []);
    setCurrentJti(ss.data?.current_jti || null);
    setSecurityActivity(sa.data?.events || []);
    const childrenList = (ch.data || []).filter((c) => !c.archived_at);
    setMediatorChildren(childrenList);
    setMediatorChildrenLoaded(true);
    // Default the new-invite child set to "all my children" so the
    // common case (single family, single solicitor) stays one-click.
    // Only overrides when no scope has been chosen yet to avoid
    // clobbering an in-progress edit.
    setMediatorChildIds((prev) => prev.length === 0 ? childrenList.map((c) => c.id) : prev);
    // Push state
    const sup = isPushSupported();
    const sub = sup ? await getPushSubscription() : null;
    setPushState({ supported: sup, subscribed: !!sub });
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (household) {
      setCur(household.currency || "EUR");
      if (household.default_split) setDefaultSplit({
        split_type: household.default_split.split_type || "50_50",
        split_payer_share: household.default_split.split_payer_share || 50,
      });
      setDefaultArrangement(household.default_parenting_arrangement || "");
    }
  }, [household]);

  // Current session pinned to top, then the rest by most recent activity
  const sortedSessions = useMemo(() => {
    const list = [...(sessions || [])];
    list.sort((a, b) => {
      if (a.is_current && !b.is_current) return -1;
      if (!a.is_current && b.is_current) return 1;
      const aT = a.last_seen_at ? new Date(a.last_seen_at).getTime() : 0;
      const bT = b.last_seen_at ? new Date(b.last_seen_at).getTime() : 0;
      return bT - aT;
    });
    return list;
  }, [sessions]);

  const copyInvite = () => {
    if (!household?.invite_code) return;
    navigator.clipboard.writeText(household.invite_code);
    toast.success("Invite code copied");
  };

  // Keep the local name draft in sync with the server-confirmed user.name
  useEffect(() => {
    if (user?.name) setNameDraft(user.name);
  }, [user?.name]);

  const saveName = async () => {
    const name = nameDraft.trim();
    if (!name || name === user?.name) return;
    setNameBusy(true);
    try {
      await api.put("/auth/profile", { name });
      await refresh?.();
      toast.success("Display name updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update name");
    } finally {
      setNameBusy(false);
    }
  };

  const openUnlink = async () => {
    setUnlinkError("");
    setUnlinkAck(false);
    setUnlinkConfirmName("");
    setUnlinkOpen(true);
    try {
      const { data } = await api.get("/household/unlink-preflight");
      setUnlinkPreflight(data);
    } catch (err) {
      setUnlinkError(err.response?.data?.detail || "Couldn't load account summary");
    }
  };
  const doUnlink = async () => {
    setUnlinkError("");
    setUnlinkBusy(true);
    try {
      await api.post("/household/unlink", {
        acknowledged: unlinkAck,
        confirm_name: unlinkConfirmName.trim() || undefined,
      });
      toast.success("Unlinked. Signing you out…");
      setTimeout(() => logout(), 1500);
    } catch (err) {
      setUnlinkError(err.response?.data?.detail || "Couldn't unlink");
    } finally {
      setUnlinkBusy(false);
    }
  };

  const regen = async () => {
    try { await api.post("/household/regenerate-invite"); await loadHousehold(); toast.success("New code generated"); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const saveCurrency = async () => {
    try { await api.put("/household/currency", { currency: cur }); await loadHousehold(); toast.success("Currency updated"); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const saveDefaultSplit = async () => {
    try {
      const payload = { split_type: defaultSplit.split_type };
      if (defaultSplit.split_type === "custom") payload.split_payer_share = Number(defaultSplit.split_payer_share);
      await api.put("/household/default-split", payload);
      await loadHousehold();
      toast.success("Default split saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const saveDefaultArrangement = async () => {
    try {
      await api.put("/household/default-arrangement", {
        default_parenting_arrangement: defaultArrangement || null,
      });
      await loadHousehold();
      toast.success(defaultArrangement ? "Default arrangement saved" : "Default arrangement cleared");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const start2fa = async () => {
    try { const { data } = await api.post("/auth/2fa/setup"); setTotpSetup(data); setTotpCode(""); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const enable2fa = async () => {
    try {
      const { data } = await api.post("/auth/2fa/enable", { code: totpCode });
      toast.success("Two-factor auth enabled");
      // v77.99.117 — Server returns 10 single-use recovery codes ONCE.
      // Display them so the user can save / print before refreshing.
      if (data?.recovery_codes?.length) setRecoveryCodes(data.recovery_codes);
      setTotpSetup(null);
      setTotpCode("");
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Invalid code"); }
  };

  const regenerateRecoveryCodes = async () => {
    if (!regenCode || regenCode.length < 6) { toast.error("Enter your 6-digit code"); return; }
    setRegenBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/recovery-codes/regenerate", { code: regenCode });
      setRecoveryCodes(data.recovery_codes || []);
      toast.success("New recovery codes generated");
      setRegenOpen(false);
      setRegenCode("");
    } catch (err) { toast.error(err.response?.data?.detail || "Invalid code"); }
    finally { setRegenBusy(false); }
  };

  const copyRecoveryCodes = async () => {
    if (!recoveryCodes?.length) return;
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      toast.success("Recovery codes copied to clipboard");
    } catch (e) {
      toast.error("Couldn't copy — please save the codes manually");
    }
  };

  const disable2fa = async () => {
    try { await api.post("/auth/2fa/disable", { code: disableCode }); toast.success("Two-factor auth disabled"); setDisableOpen(false); setDisableCode(""); await refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || "Invalid code"); }
  };

  const revokeSession = async (sid) => {
    setSessionsBusy(true);
    try {
      const { data } = await api.post(`/auth/sessions/${sid}/revoke`);
      toast.success("Signed that session out");
      if (data?.was_current) {
        // User revoked their own current session — log out locally
        await logout();
        return;
      }
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSessionsBusy(false); }
  };

  const revokeOtherSessions = async () => {
    const others = sessions.filter((s) => !s.is_current).length;
    if (others === 0) { toast.info("No other sessions are active"); return; }
    if (!window.confirm(`Sign out all ${others} other session${others === 1 ? "" : "s"}? Anyone signed into your account on another device will be signed out immediately.`)) return;
    setSessionsBusy(true);
    try {
      const { data } = await api.post("/auth/sessions/revoke-others");
      toast.success(`Signed out of ${data?.count ?? others} other session${(data?.count ?? others) === 1 ? "" : "s"}`);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSessionsBusy(false); }
  };

  const closeDelete = () => {
    if (delBusy) return;
    setDelOpen(false);
    setDelAck(false);
    setDelPassword("");
    setDelPhrase("");
    setDelError("");
  };

  // Feature request handlers
  const openFeatureRequest = () => {
    setFrError("");
    setFrSubmitted(false);
    setFrTitle("");
    setFrDesc("");
    setFrCategory("");
    setFrPriority("");
    setFrOpen(true);
  };
  const closeFeatureRequest = () => {
    if (frBusy) return;
    setFrOpen(false);
  };
  const submitFeatureRequest = async () => {
    setFrError("");
    const title = frTitle.trim();
    const description = frDesc.trim();
    if (!title) { setFrError("Please add a short title."); return; }
    if (!description) { setFrError("Please describe what you'd like us to build."); return; }
    setFrBusy(true);
    try {
      await api.post("/feature-requests", {
        title,
        description,
        category: frCategory || undefined,
        priority: frPriority || undefined,
        app_version: "0.1.0",
      });
      setFrSubmitted(true);
      // Refresh the user's list so the new submission appears immediately if they expand it
      try {
        const { data } = await api.get("/feature-requests/mine");
        setFrMine(data || []);
      } catch (err) { console.warn("[settings] refresh feature-requests failed:", err); }
    } catch (err) {
      setFrError(err.response?.data?.detail || "Couldn't submit your request");
    } finally {
      setFrBusy(false);
    }
  };
  const toggleMyRequests = async () => {
    const next = !frShowMine;
    setFrShowMine(next);
    if (next) {
      try {
        const { data } = await api.get("/feature-requests/mine");
        setFrMine(data || []);
      } catch (err) { console.warn("[settings] load feature-requests failed:", err); }
    }
  };

  const submitDeleteAccount = async () => {
    setDelError("");
    if (!delAck) { setDelError("Please tick the box confirming you understand this is permanent."); return; }
    if (!delPassword) { setDelError("Please re-enter your password."); return; }
    if (delPhrase !== "DELETE") { setDelError("Please type DELETE exactly (in capitals) to confirm."); return; }
    setDelBusy(true);
    try {
      await api.post("/auth/delete-account", {
        current_password: delPassword,
        confirmation_phrase: delPhrase,
      });
      toast.success("Your account and data have been permanently deleted");
      await logout();
    } catch (err) {
      setDelError(err.response?.data?.detail || "Could not delete account");
    } finally {
      setDelBusy(false);
    }
  };

  const submitChangePassword = async (e) => {
    e.preventDefault();
    setPwError("");
    if (!pwCurrent) { setPwError("Please enter your current password."); return; }
    if (pwNew.length < 6) { setPwError("New password must be at least 6 characters."); return; }
    if (pwNew !== pwConfirm) { setPwError("New password and confirmation don't match."); return; }
    if (pwNew === pwCurrent) { setPwError("New password must be different from the current one."); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwCurrent, new_password: pwNew });
      toast.success("Password updated");
      setPwCurrent(""); setPwNew(""); setPwConfirm("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      // v77.99.75 — Detail-shape branching extracted from nested ternary.
      let msg;
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) msg = detail[0]?.msg;
      else msg = "Failed to update password";
      setPwError(msg);
    } finally { setPwBusy(false); }
  };

  const exportBundle = () => {
    const today = new Date().toISOString().slice(0, 10);
    downloadFile(
      `${API}/export/bundle/pdf?days=${bundleDays}`,
      `coparent-bundle-${bundleDays}d-${today}.pdf`,
      { label: "Generating bundle PDF", successMessage: "PDF ready" },
    );
  };

  const createMediatorInvite = async () => {
    try {
      await api.post("/mediator/invites", {
        days: Number(mediatorDays),
        note: mediatorNote,
        permissions: mediatorPerms,
        // v77.48 — explicit per-child scope. Defaults to "all my
        // accessible children" via the load() effect above; the
        // granter can untick any they don't want this third party
        // to see.
        child_ids: mediatorChildIds,
      });
      toast.success("Third-party invite created");
      setMediatorNote("");
      load();
    }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  // v77.48 — Edit the per-child scope of an existing mediator invite.
  // Mirrors the permissions-edit flow.
  const openEditScope = (inv) => {
    setEditScopeFor(inv);
    setEditScopeIds(Array.isArray(inv.child_ids) ? [...inv.child_ids] : []);
  };
  const closeEditScope = () => { setEditScopeFor(null); setEditScopeIds([]); };
  const saveScope = async () => {
    try {
      await api.put(`/mediator/invites/${editScopeFor.id}/child-scope`, { child_ids: editScopeIds });
      toast.success("Children updated");
      closeEditScope();
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  // v77.49 — Activity-log handlers.
  const openViews = async (inv) => {
    setViewsFor(inv);
    setViewsLoading(true);
    setViews([]);
    try {
      const { data } = await api.get(`/mediator/invites/${inv.id}/views`);
      setViews(data.views || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load activity log");
    } finally {
      setViewsLoading(false);
    }
  };
  const closeViews = () => { setViewsFor(null); setViews([]); };

  const openEditPerms = (inv) => {
    setEditPermsFor(inv);
    setEditPerms({ ...(inv.permissions || {}) });
  };
  const closeEditPerms = () => { setEditPermsFor(null); setEditPerms({}); };
  const savePerms = async () => {
    try {
      await api.put(`/mediator/invites/${editPermsFor.id}/permissions`, { permissions: editPerms });
      toast.success("Permissions updated");
      closeEditPerms();
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const copyMediatorLink = (code) => {
    const link = `${window.location.origin}/mediator-signup?code=${code}`;
    navigator.clipboard.writeText(link);
    toast.success("Invite link copied");
  };

  const revokeMediator = async (id) => {
    try { await api.post(`/mediator/invites/${id}/revoke`); toast.success("Access revoked"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const togglePref = async (key, value) => {
    const next = { ...(prefs || {}), [key]: value };
    setPrefs(next);
    try { await api.put("/notifications/prefs", next); toast.success("Saved"); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); load(); }
  };

  const pauseFor = async (hours) => {
    try {
      const { data } = await api.post("/notifications/pause", { hours, until_off: false });
      setPausedUntil(data.paused_until);
      toast.success(`Paused for ${hours} hours`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const pauseUntilOff = async () => {
    try {
      const { data } = await api.post("/notifications/pause", { until_off: true });
      setPausedUntil(data.paused_until);
      toast.success("Paused until you resume");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const resumeNow = async () => {
    try { await api.post("/notifications/resume"); setPausedUntil(null); toast.success("Notifications resumed"); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const togglePush = async () => {
    try {
      if (pushState.subscribed) {
        await unsubscribeFromPush();
        setPushState({ ...pushState, subscribed: false });
        toast.success("Phone push disabled");
        return;
      }
      // v77.57 — `subscribeToPush` now returns `{ ok, reason, message }`
      // so we can show the user a precise reason (preview-host gate,
      // permission denied, no VAPID config, etc.) instead of a
      // generic "permission denied or not supported" string.
      const res = await subscribeToPush();
      if (res?.ok) {
        setPushState({ ...pushState, subscribed: true });
        toast.success("Phone push enabled");
      } else {
        toast.error(res?.message || "Couldn't enable push.");
      }
    } catch (err) { toast.error(err?.message || "Failed to configure push"); }
  };

  const pauseLabel = (() => {
    if (!pausedUntil) return null;
    if (pausedUntil === "forever") return "Paused until you resume";
    try { const dt = new Date(pausedUntil); return `Paused until ${dt.toLocaleString()}`; } catch (_) { return "Paused"; }
  })();

  // Placeholder co-parent
  const [placeholder, setPlaceholder] = useState(null);
  const [placeholderName, setPlaceholderName] = useState("");

  // v76.5 — Child-context-aware co-parent lookup. The placeholder
  // status card and the Members section both need to reflect ONLY
  // co-parents linked to the currently-selected child (per-child
  // privacy model). Fetched from /api/children/{cid}/coparents which
  // is now access-gated server-side via _require_child_access.
  const { selectedChildId, children: scopeChildren } = useChildScope();
  const [scopedCoparents, setScopedCoparents] = useState([]);
  // For the "All children" fallback, only treat someone as a real
  // co-parent if they have at least one ACTIVE child relationship.
  // /household/coparent-access is the primary's overview; co-parents
  // get a 403 there and we fall back to per-child fetches.
  const [activeCoparents, setActiveCoparents] = useState([]);
  useEffect(() => {
    let alive = true;
    if (!selectedChildId) {
      setScopedCoparents([]);
      return () => { alive = false; };
    }
    (async () => {
      try {
        const { data } = await api.get(`/children/${selectedChildId}/coparents`);
        if (alive) setScopedCoparents(data || []);
      } catch (_e) {
        if (alive) setScopedCoparents([]);
      }
    })();
    return () => { alive = false; };
  }, [selectedChildId]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/household/coparent-access");
        if (alive) {
          // Only entries with >=1 active child mean an effective co-parent
          // — anyone with zero current memberships is a stale account
          // (revoked / never granted) and shouldn't be advertised.
          setActiveCoparents((data?.coparents || []).filter(
            (c) => Array.isArray(c.child_ids) && c.child_ids.length > 0,
          ));
        }
      } catch (err) {
        if (alive && err.response?.status !== 403) {
          // Co-parents get 403 — silently fall through to scopedCoparents.
        }
      }
    })();
    return () => { alive = false; };
  }, []);

  // The "co-parent who has joined" message must reflect the current
  // child context, not whichever member happens to be in the
  // household. When a child is selected, only count co-parents who
  // have ACTIVE access to that specific child. When no child is
  // selected, fall back to the primary's "any co-parent with at
  // least one active child" list — never the raw member roster.
  const realCoParent = (() => {
    if (selectedChildId) {
      return scopedCoparents.find(
        (m) => !m.is_primary_parent && m.id !== user?.id,
      ) || null;
    }
    if (user?.is_primary_parent) {
      const first = activeCoparents.find((m) => m.id !== user?.id);
      return first || null;
    }
    // Co-parent in "All children" mode — they just see themselves and
    // the primary in the members list (privacy-scoped). The "has
    // joined" message isn't meaningful here, so suppress it.
    return null;
  })();
  const scopedCoparentNames = scopedCoparents
    .filter((m) => !m.is_primary_parent && m.id !== user?.id)
    .map((m) => m.name || m.email);
  const loadPlaceholder = useCallback(async () => {
    try { const { data } = await api.get("/household/placeholder-coparent"); setPlaceholder(data || null); }
    catch (_e) { setPlaceholder(null); }
  }, []);
  useEffect(() => { loadPlaceholder(); }, [loadPlaceholder]);

  const savePlaceholder = async () => {
    if (!placeholderName.trim()) { toast.error("Enter a name or label"); return; }
    try {
      const { data } = await api.post("/household/placeholder-coparent", { name: placeholderName.trim() });
      setPlaceholder(data);
      setPlaceholderName("");
      toast.success("Co-parent placeholder saved");
      await loadHousehold();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const removePlaceholder = async () => {
    if (!window.confirm("Remove the placeholder co-parent? Any expenses still referencing it must be reassigned or voided first.")) return;
    try {
      await api.delete("/household/placeholder-coparent");
      setPlaceholder(null);
      toast.success("Placeholder removed");
      await loadHousehold();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  // v77.34 — Promote placeholder to a real co-parent.
  // Opens a small dialog pre-filled with: the placeholder's name as
  // the invite display name, the unlinked children pre-selected, and
  // an optional email field. On submit we hit the existing per-child
  // invite endpoint with all the unlinked child_ids; the response
  // surfaces the join URL (and optionally emails the invitee).
  const unlinkedChildren = useMemo(() => {
    const linked = new Set();
    (activeCoparents || []).forEach((cp) => (cp.child_ids || []).forEach((cid) => linked.add(cid)));
    return (scopeChildren || [])
      .filter((c) => (c.status || "active") !== "archived")
      .filter((c) => !linked.has(c.id));
  }, [scopeChildren, activeCoparents]);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoteEmail, setPromoteEmail] = useState("");
  const [promoteSelectedIds, setPromoteSelectedIds] = useState([]);
  const [promoteSending, setPromoteSending] = useState(false);
  const [promoteResult, setPromoteResult] = useState(null);
  const openPromote = () => {
    setPromoteEmail("");
    setPromoteSelectedIds(unlinkedChildren.map((c) => c.id));
    setPromoteResult(null);
    setPromoteOpen(true);
  };
  const togglePromoteChild = (cid) => {
    setPromoteSelectedIds((prev) => prev.includes(cid)
      ? prev.filter((c) => c !== cid)
      : [...prev, cid]);
  };
  const submitPromote = async (e) => {
    e?.preventDefault?.();
    if (promoteSelectedIds.length === 0) { toast.error("Pick at least one child to grant access to"); return; }
    setPromoteSending(true);
    try {
      const firstId = promoteSelectedIds[0];
      const { data } = await api.post(`/children/${firstId}/invite`, {
        child_ids: promoteSelectedIds,
        email: promoteEmail.trim() || null,
      });
      setPromoteResult({
        token: data.invite?.token,
        join_url: data.join_url,
        emailed: !!data.email?.sent,
      });
      if (data.email?.sent) toast.success("Invite emailed");
      else toast.success("Invite link ready — copy and share it");
      // v77.48 — First co-parent invite milestone (Settings path).
      celebrate({
        key: "first-coparent-invite",
        userId: user?.id,
        title: "First co-parent invited",
        body: data.email?.sent
          ? "We've emailed them an invite — once they accept, you'll start sharing the selected children's records."
          : "Share the invite link with them. Once they accept, you'll start sharing the selected children's records.",
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create invite");
    } finally {
      setPromoteSending(false);
    }
  };
  const copyPromoteLink = async () => {
    if (!promoteResult?.join_url) return;
    try {
      await navigator.clipboard.writeText(promoteResult.join_url);
      toast.success("Invite link copied");
    } catch (_e) { toast.error("Couldn't copy — long-press the link to copy manually"); }
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-[0.12em] text-[#6F828A]">{user?.is_primary_admin ? "Operator" : "Co-parenting"}</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-medium mt-1">Settings</h1>
        {user?.is_primary_admin && (
          <p className="text-sm text-[#6F828A] mt-2" data-testid="operator-account-notice">
            This is the primary operator account. It is excluded from analytics and has no subscription. Use a separate test account to try the parent-facing app.
          </p>
        )}
      </div>

      {user?.is_admin && (
        <section className="border border-[#C8E0D4] bg-[#F1F8F4] rounded-md p-4 flex items-center justify-between gap-3" data-testid="admin-entry-card">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-9 h-9 rounded-sm bg-white border border-[#C8E0D4] inline-flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] uppercase tracking-[0.1em] font-semibold text-[#1F6A47]">Admin</span>
            </span>
            <div className="min-w-0">
              <div className="font-heading text-base font-medium text-[#1F3A2A]">Usage & adoption analytics</div>
              <div className="text-xs text-[#2E3A3F]">Aggregated, privacy-conscious metrics for the app operator. No user content is ever shown.</div>
            </div>
          </div>
          <Link to="/app/admin" className="shrink-0 inline-flex items-center gap-1.5 text-sm font-medium text-white bg-[#1F9FA4] hover:bg-[#178085] rounded-sm px-3 py-1.5" data-testid="admin-entry-link">
            Open dashboard
          </Link>
        </section>
      )}

      {/* v77.47 — Your children: a compact role-grouped overview so a
          user who's a managing parent for some children AND a co-parent
          for others can see their full situation in one place. */}
      <YourChildrenCard />

      {/* Subscription card — hidden for the primary admin (operator account)
          since they have no plan and don't see billing. */}
      {!user?.is_primary_admin && <SubscriptionCard />}

      <section className="border border-[#D9E1E3] bg-white rounded-md p-6">
        <h2 className="font-heading text-lg font-medium">Your account</h2>
        <div className="mt-4 grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <Label className="text-[#6F828A]">Display name</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} maxLength={80} className="max-w-xs" data-testid="account-name-input" />
              <Button onClick={saveName} disabled={nameBusy || !nameDraft.trim() || nameDraft.trim() === user?.name} className="bg-[#1F9FA4] hover:bg-[#178085] rounded-sm" data-testid="account-name-save">
                {nameBusy ? "Saving…" : "Save"}
              </Button>
            </div>
            <p className="text-xs text-[#6F828A] mt-1">Shown on messages, events and the audit log.</p>
          </div>
          <div>
            <Label className="text-[#6F828A]">Email</Label>
            <div>{user?.email}</div>
          </div>
        </div>
        <div className="mt-5 flex items-center gap-3 flex-wrap">
          <Button variant="outline" onClick={logout} className="border-[#D9E1E3] rounded-sm" data-testid="settings-logout-button">Sign out</Button>
          {!user?.is_primary_admin && (
            <Link
              to="/app/onboarding"
              className="text-sm text-[#116B6E] hover:text-[#0F5A5C] underline-offset-2 hover:underline inline-flex items-center gap-1"
              data-testid="settings-resume-onboarding"
            >
              Resume setup guide
            </Link>
          )}
        </div>
      </section>

      {/* v77.99.79 — Calendar sync card. Discoverable shortcut to the
          same .ics export the calendar header offers, with a "Last
          exported · X ago" badge so the user knows the file they
          imported earlier might be stale. Hidden for mediators (they
          have read-only PDF exports via the Court Bundle page). */}
      {user?.role !== "mediator" && <CalendarSyncCard />}

      {/* v77.6 — Placeholder co-parent section.
          Visibility rules (relationship-driven, child-context aware):
            • Primary parent only — co-parents NEVER see this section
              (a co-parent has, by definition, already joined; offering
              them a placeholder for themselves is nonsense).
            • When a child is selected, hide the input/CTA the moment a
              real co-parent has ACTIVE access to that child — the
              placeholder isn't needed anymore.
            • When in "All children" mode, hide the input the moment any
              real co-parent has joined any child — the user has at
              least one real account to attach expenses to.
          The "has joined" confirmation message is still shown when a
          real co-parent exists, so the user gets positive feedback that
          the placeholder is no longer required. The input + Add-button
          UI is the only thing gated.
          The whole section is hidden once the placeholder is no longer
          relevant AND there's nothing left to surface. */}
      {user?.is_primary_parent && !user?.is_primary_admin && (() => {
        // v77.32 — Per-child placeholder eligibility.
        // The placeholder section is useful whenever AT LEAST ONE active
        // child does not yet have a real co-parent linked. With multi-co-
        // parent households, only hide the section once every visible
        // child is fully linked.
        const activeChildren = (scopeChildren || []).filter(
          (c) => (c.status || "active") !== "archived",
        );
        const linkedChildIds = new Set();
        (activeCoparents || []).forEach((cp) => {
          (cp.child_ids || []).forEach((cid) => linkedChildIds.add(cid));
        });
        const someChildUnlinked = activeChildren.length === 0
          ? true  // pre-onboarding: no children yet, allow setup
          : activeChildren.some((c) => !linkedChildIds.has(c.id));
        // For the selected-child view we still trust the per-child scoped
        // lookup (realCoParent) — that's already correct.
        const hasConnectedCoparent = selectedChildId
          ? !!realCoParent
          : !someChildUnlinked;
        if (hasConnectedCoparent && !placeholder) return null;
        return (
        <section className="border border-[#D9E1E3] bg-white rounded-md p-6" data-testid="placeholder-section">
        <h2 className="font-heading text-lg font-medium">Co-parent</h2>
        <p className="text-sm text-[#6F828A] mt-1">Track expenses even if your co-parent hasn&apos;t joined yet — add a placeholder label for now. When they accept a per-child invite, their real account replaces the placeholder and every past expense stays linked.</p>
        {hasConnectedCoparent && realCoParent ? (
          <div className="mt-4 text-sm text-[#116B6E] bg-[#D6F0EF] border border-[#B4E0DE] rounded-sm px-3 py-2" data-testid="placeholder-coparent-joined">
            {selectedChildId && scopedCoparentNames.length > 0 ? (
              <>
                Your co-parent
                {scopedCoparentNames.length === 1 ? " " : "s "}
                <span className="font-medium">{scopedCoparentNames.join(", ")}</span>
                {scopedCoparentNames.length === 1 ? " has " : " have "}
                joined for this child. Placeholders aren&apos;t needed.
              </>
            ) : (
              <>Your co-parent <span className="font-medium">{realCoParent.name}</span> has joined. Placeholders aren&apos;t needed.</>
            )}
          </div>
        ) : placeholder ? (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border border-[#D9E1E3] rounded-sm p-3" data-testid="placeholder-current">
            <div>
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Current placeholder</div>
              <div className="font-medium text-[#2E3A3F] mt-0.5 inline-flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: placeholder.color || "#7A5AA6" }}></span>
                {placeholder.name}
              </div>
              {unlinkedChildren.length > 0 && (
                <div className="text-xs text-[#6F828A] mt-1">
                  {unlinkedChildren.length === 1
                    ? `${unlinkedChildren[0].name} doesn't have a real co-parent linked yet.`
                    : `${unlinkedChildren.length} children don't have a real co-parent linked yet.`}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {unlinkedChildren.length > 0 && (
                <Button
                  onClick={openPromote}
                  className="bg-[#1F9FA4] hover:bg-[#178085] text-white rounded-sm h-9"
                  data-testid="placeholder-promote-button"
                >
                  Promote to real co-parent
                </Button>
              )}
              <Button variant="outline" onClick={removePlaceholder} className="border-[#D9E1E3] rounded-sm text-[#6F828A] hover:text-[#C0291D] h-9" data-testid="placeholder-delete-button">Remove</Button>
            </div>
          </div>
        ) : (
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <Input value={placeholderName} onChange={(e) => setPlaceholderName(e.target.value)} placeholder="e.g. Dad, John, Other parent" className="w-[240px]" data-testid="placeholder-name-input" />
            <Button onClick={savePlaceholder} className="bg-[#1F9FA4] hover:bg-[#178085]" data-testid="placeholder-save-button">Add placeholder</Button>
          </div>
        )}
      </section>
        );
      })()}

      {/* v77.7 — Invite section. Split into two visibility tiers:
            • The "Invite a co-parent" CTA + heading appear ONLY for the
              primary parent. Co-parents cannot invite other co-parents
              and shouldn't be teased with a button that 403s.
            • The "Connected parents" list (privacy-scoped server-side)
              renders for every household member as a transparency aid:
              you should be able to see who else has access. */}
      {!user?.is_primary_admin && (
      <section className="border border-[#D9E1E3] bg-white rounded-md p-6" data-testid="invite-section">
        <h2 className="font-heading text-lg font-medium">
          {user?.is_primary_parent ? "Invite a co-parent" : "Connected parents"}
        </h2>
        {user?.is_primary_parent ? (
          <>
            <p className="text-sm text-[#6F828A] mt-1">
              Co-parents are invited <span className="font-medium text-[#2E3A3F]">per child</span>. Each invite link is single-use and only grants access to the children you choose — never the whole household.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 flex-wrap">
              <Button asChild className="bg-[#1F9FA4] hover:bg-[#178085]" data-testid="invite-go-to-children-button">
                <Link to="/app/children">Open Children to invite</Link>
              </Button>
              <span className="text-xs text-[#6F828A]">Tip: open a child profile and use the &quot;Invite co-parent&quot; button.</span>
            </div>
          </>
        ) : (
          <p className="text-sm text-[#6F828A] mt-1">
            People who share access to at least one child with you. Each managing parent can invite their own co-parents from <span className="font-medium text-[#2E3A3F]">Children → Invite co-parent</span>.
          </p>
        )}
        <div className="mt-5">
          {/* v76.5 — Section label is role-aware. Primary parents see
              "Members" (full household roster). Co-parents see
              "Connected parents" (only the primary + co-parents who
              share at least one child with them; backend already
              scopes the list per caller). */}
          <Label className="text-xs uppercase tracking-[0.1em] text-[#6F828A]">
            {user?.is_primary_parent ? "Members" : "Connected parents"}
          </Label>
          <ul className="mt-2 space-y-1 text-sm">
            {members.map((m) => (
              <li
                key={m.id}
                className="flex justify-between border-b border-[#D9E1E3] py-2 last:border-0"
                data-testid={`member-${m.id}`}
              >
                <span>
                  {m.name}
                  {m.is_placeholder && (
                    <span className="ml-2 text-[10px] uppercase tracking-[0.08em] text-[#6F828A] border border-[#D9E1E3] rounded-sm px-1.5 py-0.5">
                      Placeholder
                    </span>
                  )}
                  {m.id === user?.id && (
                    <span className="ml-2 text-[10px] uppercase tracking-[0.08em] text-[#6F828A]">You</span>
                  )}
                </span>
                <span className="text-[#6F828A]">{m.email}</span>
              </li>
            ))}
          </ul>
          {!user?.is_primary_parent && (
            <p className="text-[11px] text-[#6F828A] mt-2 italic">
              For privacy, you only see co-parents you share child access with.
            </p>
          )}
        </div>

        {realCoParent && (
          <div className="mt-6 border-t border-[#D9E1E3] pt-5" data-testid="unlink-coparent-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h3 className="font-heading text-base font-medium">Unlink co-parent</h3>
                <p className="text-sm text-[#6F828A] mt-1 max-w-xl">
                  If you unlink your co-parent, you will need to go through the linking process again to reconnect. All shared records stay with your co-parent on this account; you&apos;ll start a new, empty one.
                </p>
              </div>
              <Button variant="outline" onClick={openUnlink} className="border-[#C0291D] text-[#8C2A22] hover:bg-[#FBEFED] rounded-sm shrink-0" data-testid="unlink-coparent-button">
                Unlink co-parent
              </Button>
            </div>
          </div>
        )}
      </section>
      )}

      {/* v72 — Co-parent access overview. Primary parent only. Shows
          which co-parent has access to which child, plus pending
          invites. Lets the primary parent revoke per-child access
          (soft-delete) and see invite tokens that haven't been
          consumed yet. */}
      {user?.is_primary_parent && <CoparentAccessSection />}

      {/* v76 — My access summary. Co-parent perspective: shows the
          logged-in co-parent which children they're linked to and
          who manages that access. Trust / transparency check for
          high-conflict households. Hidden for primary parents
          (they have the full overview above) and operators. */}
      {!user?.is_primary_parent && !user?.is_primary_admin && <MyAccessSection />}

      <section className="border border-[#D9E1E3] bg-white rounded-md p-6" data-testid="notifications-section">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-heading text-lg font-medium inline-flex items-center gap-2"><Bell className="w-4 h-4 text-[#1F9FA4]" /> Notifications</h2>
            <p className="text-sm text-[#6F828A] mt-1">Per-user preferences — your co-parent&apos;s settings are separate. Pause anytime.</p>
          </div>
        </div>

        <div className={`mt-4 p-4 rounded-sm border ${pausedUntil ? "bg-[#FBF0DC] border-[#E8C29A] text-[#5C3A1E]" : "bg-[#EEF3F4] border-[#D9E1E3]"}`} data-testid="pause-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              {pausedUntil ? <BellOff className="w-4 h-4" /> : <BellRing className="w-4 h-4 text-[#1F9FA4]" />}
              <span className="font-medium text-sm" data-testid="pause-status">{pauseLabel || "Notifications are on"}</span>
            </div>
            {pausedUntil ? (
              <Button size="sm" onClick={resumeNow} className="bg-[#1F9FA4] hover:bg-[#178085] rounded-sm h-7" data-testid="notif-resume-button">Resume now</Button>
            ) : (
              <div className="flex flex-wrap gap-1.5" data-testid="pause-options">
                {[8, 24, 48, 72].map((h) => (
                  <Button key={h} size="sm" variant="outline" onClick={() => pauseFor(h)} className="border-[#D9E1E3] rounded-sm h-7" data-testid={`notif-pause-${h}h-button`}>Pause {h}h</Button>
                ))}
                <Button size="sm" variant="outline" onClick={pauseUntilOff} className="border-[#D9E1E3] rounded-sm h-7" data-testid="notif-pause-until-off-button">Until I turn it back on</Button>
              </div>
            )}
          </div>
        </div>

        {prefs && (
          <div className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="notif-prefs-grid">
            <PrefRow label="New messages" sub="When your co-parent writes to you" value={!!prefs.messages} onChange={(v) => togglePref("messages", v)} testid="pref-messages" />
            <PrefRow label="Change requests" sub="When a request is made or awaiting your review" value={!!prefs.change_requests} onChange={(v) => togglePref("change_requests", v)} testid="pref-change-requests" />
            <PrefRow label="Calendar reminders & events" sub="Upcoming events & your personal reminders" value={!!prefs.calendar_reminders} onChange={(v) => togglePref("calendar_reminders", v)} testid="pref-calendar-reminders" />
            <PrefRow label="Expense updates" sub="New expenses, outstanding, payments recorded" value={!!prefs.expense_updates} onChange={(v) => togglePref("expense_updates", v)} testid="pref-expense-updates" />
            <PrefRow label="Call / video call requests" sub="Incoming call event notifications" value={!!prefs.calls} onChange={(v) => togglePref("calls", v)} testid="pref-calls" />
            <PrefRow label="Agreed / not agreed requests" sub="When your co-parent responds to one of your requests" value={!!prefs.request_decisions} onChange={(v) => togglePref("request_decisions", v)} testid="pref-request-decisions" />
            <PrefRow label="Journal entries shared with you" sub="When your co-parent shares a journal item" value={!!prefs.journal_shares} onChange={(v) => togglePref("journal_shares", v)} testid="pref-journal-shares" />
            <PrefRow label="Documents shared with you" sub="When your co-parent shares a document" value={!!prefs.document_shares} onChange={(v) => togglePref("document_shares", v)} testid="pref-document-shares" />
          </div>
        )}

        <div className="mt-5 border-t border-[#D9E1E3] pt-4" data-testid="push-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="font-medium text-sm">Phone push (Web Push)</div>
              <p className="text-xs text-[#6F828A] mt-1 max-w-xl">
                Install CoParent Console to your home screen to receive lock-screen notifications. Works on Android Chrome and iOS 16.4+ (when installed as an app). Your preferences above still apply.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {!pushState.supported && <span className="text-xs text-[#6F828A]">Not supported on this device</span>}
              {pushState.supported && (
                <Button size="sm" variant={pushState.subscribed ? "outline" : "default"} onClick={togglePush} className={pushState.subscribed ? "border-[#D9E1E3] rounded-sm h-8" : "bg-[#1F9FA4] hover:bg-[#178085] rounded-sm h-8"} data-testid="push-toggle-button">
                  {pushState.subscribed ? "Disable push" : "Enable push"}
                </Button>
              )}
            </div>
          </div>
        </div>
        {/* v77.99.140 (iter 124.monthly-summary) — Opt-out for the
            end-of-month statement email. Default ON; users can turn
            it off individually. See routes/monthly_summary.py. */}
        <MonthlySummaryToggle />
      </section>

      {/* v77.99.68 — Reset Dashboard prompts. Hatch for users who
          dismissed too quickly, and for operators / testers running
          through the onboarding flow more than once. Re-arms the
          split-revisit and schedule-revisit prompts when their
          conditions are still true, and clears the client-side
          sole-care confirmation flag for this user-id. */}
      <section className="border border-[#D9E1E3] bg-white rounded-md p-6" data-testid="reset-dashboard-prompts-section">
        <h2 className="font-heading text-lg font-medium">Dashboard prompts</h2>
        <p className="text-sm text-[#6F828A] mt-1">One-time banners that confirm sole-care mode, or appear when a co-parent joins (to switch the default expense split, or finish setting up your saved schedule pattern). Reset them if you dismissed too quickly.</p>
        <div className="mt-4">
          <Button
            type="button"
            variant="outline"
            className="border-[#A7D8DA] text-[#116B6E] hover:bg-[#E0F2F2]"
            data-testid="reset-dashboard-prompts-button"
            onClick={async () => {
              try {
                const r = await api.post("/household/reset-dashboard-prompts", {});
                // Always clear the per-user sole-care dismissal flag too,
                // since that lives in localStorage and the backend can't
                // touch it.
                try {
                  Object.keys(localStorage)
                    .filter((k) => k.startsWith("cpc_sole_care_card_dismissed_"))
                    .forEach((k) => localStorage.removeItem(k));
                } catch (e) { console.debug("[settings] localStorage cleanup failed:", e?.message); }
                const reArmed = (r.data?.re_armed) || [];
                if (reArmed.length === 0) {
                  toast.success("Reset. No prompts to re-arm right now. If a co-parent joins later you'll see them again.");
                } else {
                  const labels = reArmed.map((k) => k === "split" ? "expense split" : k === "schedule" ? "schedule" : k).join(" + ");
                  toast.success(`Reset. We re-armed: ${labels}. Head back to the Dashboard to see them.`);
                }
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Could not reset prompts.");
              }
            }}
          >
            Reset prompts
          </Button>
        </div>
      </section>



      {/* v77.7 — Currency + Default split are household-level
          settings. Only the primary parent can change them — co-parents
          inherit. Hide the entire UI rather than show a 403 toast. */}
      {user?.is_primary_parent && !user?.is_primary_admin && (
      <section className="border border-[#D9E1E3] bg-white rounded-md p-6" data-testid="currency-section">
        <h2 className="font-heading text-lg font-medium">Currency</h2>
        <p className="text-sm text-[#6F828A] mt-1">Used across expenses, summaries and reports. Shared by both co-parents.</p>
        <div className="mt-4 flex items-center gap-2">
          <Select value={cur} onValueChange={setCur}>
            <SelectTrigger className="w-[200px]" data-testid="currency-select"><SelectValue /></SelectTrigger>
            <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={saveCurrency} className="bg-[#1F9FA4] hover:bg-[#178085]" data-testid="currency-save-button">Save</Button>
        </div>
      </section>
      )}

      {user?.is_primary_parent && !user?.is_primary_admin && (
      <section className="border border-[#D9E1E3] bg-white rounded-md p-6" data-testid="default-split-section">
        <h2 className="font-heading text-lg font-medium">Default expense split</h2>
        <p className="text-sm text-[#6F828A] mt-1">Applied when a new expense uses &quot;Agreed default&quot;. Overridable per expense.</p>
        <div className="mt-4 grid sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
          <div>
            <Label>Split</Label>
            <Select value={defaultSplit.split_type} onValueChange={(v) => setDefaultSplit({ ...defaultSplit, split_type: v })}>
              <SelectTrigger data-testid="default-split-select"><SelectValue /></SelectTrigger>
              <SelectContent>{SPLIT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {defaultSplit.split_type === "custom" && (
            <div>
              <Label>Payer share %</Label>
              <Input type="number" min={0} max={100} value={defaultSplit.split_payer_share} onChange={(e) => setDefaultSplit({ ...defaultSplit, split_payer_share: e.target.value })} className="w-[120px]" data-testid="default-split-share" />
            </div>
          )}
          <Button onClick={saveDefaultSplit} className="bg-[#1F9FA4] hover:bg-[#178085]" data-testid="default-split-save-button">Save</Button>
        </div>
        {/* v77.99.64 — Note attached to the 100/0 preset so users
            understand why it was selected and that it is not
            auto-reverted when a co-parent joins later. Honest
            disclosure without nagging. */}
        {defaultSplit.split_type === "100_0" && (
          <div
            className="mt-4 text-[12.5px] text-[#6F4E1E] bg-[#FBF5E8] border border-[#EADFC9] rounded-sm p-3 leading-relaxed"
            data-testid="default-split-100-0-note"
          >
            <strong className="font-medium">About 100 / 0:</strong> the payer keeps the whole cost of each expense and no reimbursement is implied. This is set automatically when you pick &quot;No current shared parenting time&quot; during onboarding, but it stays in place even if a co-parent or placeholder joins later. Switch to a shared split (50 / 50, 60 / 40, custom) any time once that changes.
          </div>
        )}
      </section>
      )}

      {/* v77.99.9 — Household-default parenting arrangement. Drives the
          dashboard's adaptive-relevance logic so non-shared-schedule
          households get calmer copy + reordered cards. Per-child overrides
          live on each child's profile. Primary parent only. */}
      {user?.is_primary_parent && !user?.is_primary_admin && (
      <section className="border border-[#D9E1E3] bg-white rounded-md p-6" data-testid="default-arrangement-section">
        <h2 className="font-heading text-lg font-medium">Default parenting arrangement</h2>
        <p className="text-sm text-[#6F828A] mt-1 leading-relaxed">
          Tells the dashboard whether to show schedule prompts for this household.
          Pick a single default that fits most of your children — you can still set
          a different arrangement on individual child profiles if one stands apart.
        </p>
        <div className="mt-4 grid sm:grid-cols-[1fr_auto] gap-3 items-end max-w-2xl">
          <div>
            <Label>Arrangement</Label>
            <Select
              value={defaultArrangement || "__auto__"}
              onValueChange={(v) => setDefaultArrangement(v === "__auto__" ? "" : v)}
            >
              <SelectTrigger data-testid="default-arrangement-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__auto__" data-testid="default-arrangement-option-auto">
                  Auto · derive from each child
                </SelectItem>
                {ARRANGEMENT_TYPES.map((a) => (
                  <SelectItem key={a.value} value={a.value} data-testid={`default-arrangement-option-${a.value}`}>
                    {a.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {defaultArrangement && (
              <p className="text-[12px] text-[#4A5560] mt-2 italic">
                {ARRANGEMENT_TYPES.find((a) => a.value === defaultArrangement)?.description}
              </p>
            )}
          </div>
          <Button
            onClick={saveDefaultArrangement}
            className="bg-[#1F9FA4] hover:bg-[#178085]"
            data-testid="default-arrangement-save-button"
          >
            Save
          </Button>
        </div>
      </section>
      )}

      {/* v77.8 — Read-only "Household settings" panel for co-parents.
          The currency + default split are set by the primary parent and
          shared across the account. Co-parents inherit them, but they
          should still be able to SEE what's in effect (otherwise they'd
          have to ask "what currency are we using?" before logging an
          expense). The panel is informational — no edit affordance. */}
      {!user?.is_primary_parent && !user?.is_primary_admin && (
        <section className="border border-[#D9E1E3] bg-[#FAFBFB] rounded-md p-6" data-testid="household-settings-readonly">
          <h2 className="font-heading text-lg font-medium">Household settings</h2>
          <p className="text-sm text-[#6F828A] mt-1">Set by the primary parent and shared across the account.</p>
          <dl className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Currency</dt>
              <dd className="mt-1 font-mono-data text-[#2E3A3F]" data-testid="household-readonly-currency">
                {(household?.currency || currency || "EUR").toUpperCase()}
              </dd>
            </div>
            <div>
              <dt className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Default expense split</dt>
              <dd className="mt-1 text-[#2E3A3F]" data-testid="household-readonly-default-split">
                {(() => {
                  const ds = household?.default_split || {};
                  const t = ds.split_type || "50_50";
                  const labels = { "50_50": "50 / 50", "60_40": "60 / 40", "70_30": "70 / 30", "100_0": "100 / 0 (sole care)" };
                  if (t === "custom") {
                    const share = ds.split_payer_share ?? 50;
                    return `Custom — payer ${share}% / co-parent ${100 - share}%`;
                  }
                  return labels[t] || t;
                })()}
              </dd>
            </div>
            <div>
              <dt className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Parenting arrangement</dt>
              <dd className="mt-1 text-[#2E3A3F]" data-testid="household-readonly-arrangement">
                {ARRANGEMENT_LABEL[household?.default_parenting_arrangement] || "Auto (derived per child)"}
              </dd>
            </div>
          </dl>
        </section>
      )}

      <section className="border border-[#D9E1E3] bg-white rounded-md p-6" data-testid="twofa-section">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-heading text-lg font-medium">Two-factor authentication</h2>
            <p className="text-sm text-[#6F828A] mt-1">Use an authenticator app (Google Authenticator, 1Password, Authy) for an extra layer of security on sign-in.</p>
          </div>
          {user?.totp_enabled ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 border border-[#1F9FA4] bg-[#D6F0EF] text-[#116B6E] rounded-sm text-xs"><ShieldCheck className="w-3.5 h-3.5" /> Enabled</span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 border border-[#D9E1E3] bg-[#EEF3F4] text-[#6F828A] rounded-sm text-xs"><ShieldOff className="w-3.5 h-3.5" /> Disabled</span>
          )}
        </div>
        <div className="mt-4">
          {!user?.totp_enabled && !totpSetup && (
            <Button onClick={start2fa} className="bg-[#1F9FA4] hover:bg-[#178085]" data-testid="twofa-setup-button">Set up 2FA</Button>
          )}
          {!user?.totp_enabled && totpSetup && (
            <div className="grid sm:grid-cols-[auto_1fr] gap-6 items-start">
              <img src={`data:image/png;base64,${totpSetup.qr_png_base64}`} alt="2FA QR" className="w-40 h-40 border border-[#D9E1E3] rounded-sm" data-testid="twofa-qr" />
              <div className="space-y-3">
                <div className="text-xs text-[#6F828A] uppercase tracking-[0.1em]">Or enter this secret manually</div>
                <div className="font-mono-data tracking-widest text-sm p-2 border border-[#D9E1E3] bg-[#EEF3F4] rounded-sm break-all" data-testid="twofa-secret">{totpSetup.secret}</div>
                <div>
                  <Label>Enter the 6-digit code to confirm</Label>
                  <Input value={totpCode} onChange={(e) => setTotpCode(e.target.value)} inputMode="numeric" maxLength={6} data-testid="twofa-verify-input" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={enable2fa} className="bg-[#1F9FA4] hover:bg-[#178085]" data-testid="twofa-enable-button">Verify & enable</Button>
                  <Button variant="ghost" onClick={() => setTotpSetup(null)} data-testid="twofa-cancel-button">Cancel</Button>
                </div>
              </div>
            </div>
          )}
          {user?.totp_enabled && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => setDisableOpen(true)} className="border-[#D9E1E3] rounded-sm" data-testid="twofa-disable-button">Disable 2FA</Button>
                <Button variant="outline" onClick={() => { setRegenOpen(true); setRegenCode(""); }} className="border-[#D9E1E3] rounded-sm" data-testid="twofa-regen-codes-button">Regenerate recovery codes</Button>
              </div>
              <p className="text-xs text-[#6F828A]">
                Recovery codes are single-use backup codes you can use if you lose access to your authenticator app. Keep them somewhere safe.
              </p>
            </div>
          )}
        </div>
        {recoveryCodes && (
          <div className="mt-4 border border-[#A6D7D9] bg-[#E0F2F2] rounded-sm p-4" data-testid="twofa-recovery-codes-panel">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="font-heading text-sm font-medium text-[#116B6E]">Save these recovery codes now</div>
                <p className="text-xs text-[#3F595B] mt-1">
                  Each code can be used once. They will not be shown again. Print them or store them in a password manager — anyone with one of these codes can sign in to your account.
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={copyRecoveryCodes} className="border-[#A6D7D9] rounded-sm" data-testid="twofa-recovery-copy">Copy</Button>
                <Button variant="ghost" size="sm" onClick={() => setRecoveryCodes(null)} data-testid="twofa-recovery-dismiss">I&apos;ve saved them</Button>
              </div>
            </div>
            <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 font-mono-data text-sm text-[#0B2E32]" data-testid="twofa-recovery-codes-list">
              {recoveryCodes.map((c) => (
                <li key={c} className="tracking-widest">{c}</li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section className="border border-[#D9E1E3] bg-white rounded-md p-6" data-testid="change-password-section">
        <div className="flex items-start gap-3">
          <span className="w-8 h-8 rounded-sm bg-[#E0F2F2] text-[#116B6E] inline-flex items-center justify-center flex-shrink-0 mt-0.5">
            <KeyRound className="w-4 h-4" strokeWidth={1.5} />
          </span>
          <div>
            <h2 className="font-heading text-lg font-medium">Change password</h2>
            <p className="text-sm text-[#6F828A] mt-1">Update the password you use to sign in. Your current password is required to confirm the change.</p>
          </div>
        </div>
        <form onSubmit={submitChangePassword} noValidate className="mt-4 space-y-4 max-w-md">
          <div>
            <Label htmlFor="pw-current">Current password</Label>
            <PasswordInput
              id="pw-current"
              value={pwCurrent}
              onChange={(e) => { setPwCurrent(e.target.value); setPwError(""); }}
              autoComplete="current-password"
              required
              data-testid="change-password-current"
            />
          </div>
          <div>
            <Label htmlFor="pw-new">New password</Label>
            <PasswordInput
              id="pw-new"
              minLength={6}
              value={pwNew}
              onChange={(e) => { setPwNew(e.target.value); setPwError(""); }}
              autoComplete="new-password"
              required
              data-testid="change-password-new"
            />
            <div className="text-xs text-[#6F828A] mt-1">At least 6 characters.</div>
          </div>
          <div>
            <Label htmlFor="pw-confirm">Confirm new password</Label>
            <PasswordInput
              id="pw-confirm"
              minLength={6}
              value={pwConfirm}
              onChange={(e) => { setPwConfirm(e.target.value); setPwError(""); }}
              autoComplete="new-password"
              required
              data-testid="change-password-confirm"
            />
          </div>
          {pwError && (
            <div className="text-sm text-[#8C2A22] bg-[#FBEFED] border border-[#E8B8B2] rounded-sm px-3 py-2" data-testid="change-password-error">
              {pwError}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              type="submit"
              disabled={pwBusy}
              className="bg-[#1F9FA4] hover:bg-[#178085] rounded-sm"
              data-testid="change-password-submit"
            >
              {pwBusy ? "Updating…" : "Update password"}
            </Button>
            {(pwCurrent || pwNew || pwConfirm) && !pwBusy && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => { setPwCurrent(""); setPwNew(""); setPwConfirm(""); setPwError(""); }}
                data-testid="change-password-clear"
              >
                Clear
              </Button>
            )}
          </div>
        </form>
      </section>

      <section className="border border-[#D9E1E3] bg-white rounded-md p-6" data-testid="sessions-section">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <span className="w-8 h-8 rounded-sm bg-[#E0F2F2] text-[#116B6E] inline-flex items-center justify-center flex-shrink-0 mt-0.5">
              <Monitor className="w-4 h-4" strokeWidth={1.5} />
            </span>
            <div>
              <h2 className="font-heading text-lg font-medium">Where you&apos;re signed in</h2>
              <p className="text-sm text-[#6F828A] mt-1">Devices and browsers currently signed in to your account. If you see anything you don&apos;t recognise, sign it out — they will be logged out immediately and must sign in again.</p>
            </div>
          </div>
          {sessions.filter((s) => !s.is_current).length > 0 && (
            <Button
              type="button"
              onClick={revokeOtherSessions}
              disabled={sessionsBusy}
              variant="outline"
              className="border-[#8C2A22] text-[#8C2A22] hover:bg-[#FBEFED] rounded-sm"
              data-testid="sessions-revoke-others"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign out everywhere else
            </Button>
          )}
        </div>
        <ul className="mt-4 divide-y divide-[#D9E1E3] border border-[#D9E1E3] rounded-md overflow-hidden max-h-[300px] sm:max-h-[360px] overflow-y-auto" data-testid="sessions-list">
          {sessions.length === 0 && (
            <li className="p-6 text-center text-sm text-[#6F828A]" data-testid="sessions-empty">
              No active sessions yet. Your current sign-in will appear here on your next visit.
            </li>
          )}
          {sortedSessions.map((s) => (
            <li key={s.id} className={`p-4 flex items-start justify-between gap-3 flex-wrap ${s.is_current ? "bg-[#F2FAFA] border-l-2 border-l-[#1F9FA4]" : "bg-white"}`} data-testid={`session-row-${s.id}`}>
              <div className="flex items-start gap-3 min-w-0">
                <Monitor className="w-4 h-4 mt-1 text-[#6F828A]" strokeWidth={1.5} />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[#2E3A3F]">{s.device_label}</span>
                    {s.is_current && (
                      <span className="text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border border-[#1F9FA4] bg-[#D6F0EF] text-[#116B6E] rounded-sm" data-testid={`session-current-${s.id}`}>This device · Active now</span>
                    )}
                  </div>
                  <div className="text-xs text-[#6F828A] font-mono-data mt-1 space-x-2">
                    <span>Last active {relativeTime(s.last_seen_at)}</span>
                    {s.ip && <span>· {s.ip}</span>}
                    {s.created_at && <span>· Signed in {relativeTime(s.created_at)}</span>}
                  </div>
                </div>
              </div>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => revokeSession(s.id)}
                disabled={sessionsBusy}
                className="text-[#8C2A22] hover:bg-[#FBEFED] rounded-sm"
                data-testid={`session-revoke-${s.id}`}
              >
                {s.is_current ? "Sign out" : "Sign out of this device"}
              </Button>
            </li>
          ))}
        </ul>
        {sessions.length > 10 && (
          <div className="mt-2 text-xs text-[#6F828A] text-center" data-testid="sessions-count-note">
            Showing all {sessions.length} active sessions — scroll within the list to see older devices.
          </div>
        )}

        {securityActivity.length > 0 && (
          <div className="mt-6" data-testid="security-activity-block">
            <h3 className="text-xs uppercase tracking-[0.12em] text-[#6F828A] font-medium">Recent security activity</h3>
            <ul className="mt-2 space-y-1.5">
              {securityActivity.map((e, i) => (
                <li key={`${e.at}-${i}`} className="flex items-center justify-between gap-3 text-sm" data-testid={`security-activity-${i}`}>
                  <span className="text-[#2E3A3F]">{e.label}</span>
                  <span className="text-xs text-[#6F828A] font-mono-data whitespace-nowrap">{relativeTime(e.at)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section id="third-party" className="border border-[#D9E1E3] bg-white rounded-md p-6 scroll-mt-24" data-testid="mediator-section">
        <div className="flex items-start gap-3">
          <span className="w-8 h-8 rounded-sm bg-[#E0F2F2] text-[#116B6E] inline-flex items-center justify-center flex-shrink-0 mt-0.5">
            <UserCog className="w-4 h-4" strokeWidth={1.5} />
          </span>
          <div className="max-w-2xl">
            <h2 className="font-heading text-lg font-semibold text-[#2E3A3F] leading-tight">Third-party access</h2>
            <div className="text-sm text-[#6F828A] mt-0.5">Mediator / Solicitor / Guardian ad Litem</div>
            <p className="text-sm text-[#6F828A] mt-3">Control exactly what this third party can view. You can revoke access at any time.</p>
            <p className="text-xs text-[#116B6E] mt-2 inline-flex items-center gap-1.5 bg-[#D6F0EF] border border-[#B4E0DE] rounded-sm px-2 py-1">
              <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.75} /> All access is view-only. No actions can be taken within the app.
            </p>
          </div>
        </div>

        {/* Create-invite form */}
        <div className="mt-5 border border-[#D9E1E3] rounded-md p-4 bg-[#F5F7F8]" data-testid="mediator-create-form">
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <Label>Access duration (days)</Label>
              <Input type="number" min={1} max={365} value={mediatorDays} onChange={(e) => setMediatorDays(e.target.value)} className="w-[140px] bg-white" data-testid="mediator-days-input" />
            </div>
            <div className="flex-1 min-w-[220px]">
              <Label>Internal note (optional)</Label>
              <Input value={mediatorNote} onChange={(e) => setMediatorNote(e.target.value)} placeholder="e.g. Solicitor — Smith & Co" className="bg-white" data-testid="mediator-note-input" />
            </div>
          </div>

          <div className="mt-5">
            <div className="text-xs uppercase tracking-[0.12em] text-[#1F9FA4] font-semibold">Which children can they see?</div>
            <p className="text-xs text-[#6F828A] mt-1">
              Pick exactly which children this third party will be able to view. They&apos;ll see no data — messages, calendar, expenses, journal — for any unticked child. You can only grant access to children you manage or co-parent on.
            </p>
            {!mediatorChildrenLoaded ? (
              <div className="mt-3 text-xs text-[#6F828A] italic" data-testid="mediator-children-loading">
                Loading your children…
              </div>
            ) : mediatorChildren.length === 0 ? (
              <div className="mt-3 text-xs text-[#6F828A] italic" data-testid="mediator-children-empty">
                No children available to share. Add a child first from the Children page.
              </div>
            ) : (
              <div className="mt-3 bg-white border border-[#D9E1E3] rounded-sm p-3" data-testid="mediator-children-picker">
                <div className="flex items-center justify-between mb-2 pb-2 border-b border-[#D9E1E3]">
                  <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">
                    {mediatorChildIds.length} of {mediatorChildren.length} selected
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="text-xs text-[#116B6E] hover:underline"
                      onClick={() => setMediatorChildIds(mediatorChildren.map((c) => c.id))}
                      data-testid="mediator-select-all-children"
                    >
                      Select all
                    </button>
                    <span className="text-[#D9E1E3]">·</span>
                    <button
                      type="button"
                      className="text-xs text-[#825A41] hover:underline"
                      onClick={() => setMediatorChildIds([])}
                      data-testid="mediator-clear-children"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="space-y-1">
                  {mediatorChildren.map((c) => {
                    const checked = mediatorChildIds.includes(c.id);
                    return (
                      <label
                        key={c.id}
                        className="flex items-center gap-2.5 py-1 cursor-pointer"
                        data-testid={`mediator-child-row-${c.id}`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => {
                            setMediatorChildIds((prev) => v
                              ? [...prev, c.id]
                              : prev.filter((x) => x !== c.id));
                          }}
                          data-testid={`mediator-child-checkbox-${c.id}`}
                        />
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color || "#1F9FA4" }} />
                        <span className="text-sm text-[#2E3A3F]">{c.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="mt-5">
            <div className="text-xs uppercase tracking-[0.12em] text-[#1F9FA4] font-semibold">Permissions</div>
            <p className="text-xs text-[#6F828A] mt-1">Core areas are on by default. Sensitive areas are off by default — enable only if needed.</p>

            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">Core</div>
              <div className="bg-white border border-[#D9E1E3] rounded-sm px-4" data-testid="mediator-perms-core">
                {CORE_PERMS.map((p) => (
                  <PermRow
                    key={p.key}
                    perm={p}
                    value={mediatorPerms[p.key]}
                    onChange={(v) => setMediatorPerms({ ...mediatorPerms, [p.key]: v })}
                    testid={`mediator-perm-${p.key}`}
                  />
                ))}
              </div>
            </div>

            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">Optional — sensitive</div>
              <div className="bg-white border border-[#E8B8B2]/60 rounded-sm px-4" data-testid="mediator-perms-optional">
                {OPTIONAL_PERMS.map((p) => (
                  <PermRow
                    key={p.key}
                    perm={p}
                    value={mediatorPerms[p.key]}
                    onChange={(v) => setMediatorPerms({ ...mediatorPerms, [p.key]: v })}
                    testid={`mediator-perm-${p.key}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="mt-5 flex justify-end">
            <Button onClick={createMediatorInvite} className="bg-[#1F9FA4] hover:bg-[#178085]" data-testid="mediator-create-button">Create invite</Button>
          </div>
        </div>

        {mediatorInvites.length > 0 && (
          <ul className="mt-5 divide-y divide-[#D9E1E3]">
            {mediatorInvites.map((inv) => {
              const expired = inv.expires_at && inv.expires_at < new Date().toISOString();
              // v77.99.75 — Status derivation extracted from a 3-level
              // nested ternary into a small switch for clarity.
              let status;
              if (inv.revoked) status = "revoked";
              else if (expired) status = "expired";
              else if (inv.used_by_user_id) status = "active";
              else status = "pending";
              const STATUS_CLASSES = {
                active: "border-[#1F9FA4] bg-[#D6F0EF] text-[#116B6E]",
                pending: "border-[#4A6170] bg-[#E6ECEF] text-[#2E3E48]",
              };
              const statusClass = STATUS_CLASSES[status] || "border-[#825A41] bg-[#F0E8E3] text-[#4A3A2E]";
              const activeCount = Object.values(inv.permissions || {}).filter(Boolean).length;
              const canEditPerms = !inv.revoked && !expired;
              return (
                <li key={inv.id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`mediator-invite-${inv.id}`}>
                  <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm ${statusClass}`}>{status}</span>
                  <div className="flex-1 min-w-0">
                    {inv.used_by_name ? (
                      <div className="text-sm text-[#2E3A3F]">{inv.used_by_name} <span className="text-[#6F828A]">({inv.used_by_email})</span></div>
                    ) : (
                      <div className="text-sm text-[#2E3A3F]">Code <span className="font-mono-data tracking-widest">{inv.code}</span></div>
                    )}
                    <div className="text-xs text-[#6F828A] font-mono-data mt-0.5">
                      Expires {formatDT(inv.expires_at)} · {inv.days}d access · {activeCount}/9 permissions
                    </div>
                    {/* v77.48 — Child scope summary. Renders the
                        names of the children this third party can
                        see, or "No children" if the scope is empty. */}
                    {Array.isArray(inv.child_ids) && (
                      <div className="text-[11px] text-[#3F595B] mt-1" data-testid={`mediator-invite-scope-${inv.id}`}>
                        <span className="text-[#6F828A]">Children: </span>
                        {inv.child_ids.length === 0 ? (
                          <span className="italic text-[#825A41]">none granted</span>
                        ) : inv.child_ids.length === mediatorChildren.length && mediatorChildren.length > 0 ? (
                          <span>All ({inv.child_ids.length})</span>
                        ) : (
                          <span>
                            {inv.child_ids
                              .map((cid) => (mediatorChildren.find((c) => c.id === cid) || { name: "(removed)" }).name)
                              .join(", ")}
                          </span>
                        )}
                      </div>
                    )}
                    {inv.note && <div className="text-xs text-[#6F828A] mt-0.5 italic">“{inv.note}”</div>}
                  </div>
                  {canEditPerms && (
                    <Button size="sm" variant="outline" className="border-[#D9E1E3] rounded-sm" onClick={() => openEditScope(inv)} data-testid={`mediator-edit-scope-${inv.id}`}>
                      Edit children
                    </Button>
                  )}
                  {canEditPerms && (
                    <Button size="sm" variant="outline" className="border-[#D9E1E3] rounded-sm" onClick={() => openEditPerms(inv)} data-testid={`mediator-edit-perms-${inv.id}`}>
                      Edit permissions
                    </Button>
                  )}
                  {/* v77.49 — Audit trail. Shows what the third party
                      actually viewed, with timestamps. Lets the
                      granter attest in court that their solicitor
                      reviewed specific evidence on a specific date. */}
                  {canEditPerms && inv.used_by_user_id && (
                    <Button size="sm" variant="outline" className="border-[#D9E1E3] rounded-sm" onClick={() => openViews(inv)} data-testid={`mediator-views-${inv.id}`}>
                      Activity log
                    </Button>
                  )}
                  {status === "pending" && (
                    <Button size="sm" variant="outline" className="border-[#D9E1E3] rounded-sm" onClick={() => copyMediatorLink(inv.code)} data-testid={`mediator-copy-${inv.id}`}>
                      <Copy className="w-3.5 h-3.5 mr-1" /> Copy link
                    </Button>
                  )}
                  {(status === "pending" || status === "active") && (
                    <Button size="sm" variant="ghost" className="text-[#825A41] hover:bg-[#F0E8E3]" onClick={() => revokeMediator(inv.id)} data-testid={`mediator-revoke-${inv.id}`}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Revoke
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* v77.34 — Promote placeholder to real co-parent dialog. Pre-fills the
          per-child invite for every unlinked child using the placeholder's
          name. Reuses POST /children/{id}/invite — when the invitee
          accepts, the existing _merge_placeholder_into_user routine
          rewrites every prior expense / event / settlement reference. */}
      <Dialog open={promoteOpen} onOpenChange={(v) => { if (!v) { setPromoteOpen(false); setPromoteResult(null); } }}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3] max-w-lg" data-testid="promote-placeholder-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Promote to real co-parent</DialogTitle>
            <DialogDescription className="text-[#6F828A]">
              Send a per-child invite. When they accept, your placeholder <span className="font-medium text-[#2E3A3F]">{placeholder?.name || ""}</span> is replaced by their real account and every existing expense / event / settlement stays linked.
            </DialogDescription>
          </DialogHeader>
          {promoteResult ? (
            <div className="space-y-4 px-1">
              <div className="bg-[#D6F0EF] border border-[#B4E0DE] rounded-sm p-3 text-sm text-[#116B6E] flex items-start gap-2" data-testid="promote-success">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  {promoteResult.emailed
                    ? "Invite emailed. They'll receive a join link valid for 14 days."
                    : "Invite link ready. Copy it and share via your preferred channel."}
                </div>
              </div>
              {promoteResult.join_url && (
                <div>
                  <Label className="text-xs uppercase tracking-[0.1em] text-[#6F828A]">Join link</Label>
                  <div className="mt-1.5 flex items-center gap-2">
                    <Input readOnly value={promoteResult.join_url} className="bg-[#FAFBFB] font-mono-data text-xs" data-testid="promote-join-link" />
                    <Button variant="outline" onClick={copyPromoteLink} className="border-[#D9E1E3] rounded-sm flex-shrink-0" data-testid="promote-copy-link"><Copy className="w-4 h-4 mr-1.5" />Copy</Button>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => { setPromoteOpen(false); setPromoteResult(null); }} className="border-[#D9E1E3] rounded-sm" data-testid="promote-close-button">Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={submitPromote} className="space-y-4 px-1" data-testid="promote-placeholder-form">
              <div>
                <Label htmlFor="promote-email">Email <span className="text-[#6F828A] font-normal">(optional)</span></Label>
                <Input
                  id="promote-email"
                  type="email"
                  value={promoteEmail}
                  onChange={(e) => setPromoteEmail(e.target.value)}
                  placeholder="them@example.com"
                  className="mt-1.5"
                  data-testid="promote-email-input"
                />
                <p className="text-xs text-[#6F828A] mt-1">If provided we&apos;ll email them the invite. Otherwise you&apos;ll get a copyable link.</p>
              </div>
              <div>
                <Label>Children they&apos;ll have access to</Label>
                {unlinkedChildren.length === 0 ? (
                  <div className="mt-1.5 text-sm text-[#6F828A]">Every child is already linked to a real co-parent. Nothing left to promote.</div>
                ) : (
                  <div className="mt-1.5 space-y-1.5" data-testid="promote-children-list">
                    {unlinkedChildren.map((c) => {
                      const checked = promoteSelectedIds.includes(c.id);
                      return (
                        <label key={c.id} className="flex items-center gap-2.5 px-3 py-2 border border-[#EEF3F4] rounded-sm cursor-pointer hover:bg-[#FAFBFB]" data-testid={`promote-child-${c.id}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => togglePromoteChild(c.id)}
                            className="w-4 h-4 accent-[#1F9FA4]"
                          />
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color || "#7A5AA6" }}></span>
                          <span className="text-sm text-[#2E3A3F]">{c.name}</span>
                          <span className="ml-auto text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">No co-parent</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPromoteOpen(false)} className="border-[#D9E1E3] rounded-sm" data-testid="promote-cancel-button">Cancel</Button>
                <Button
                  type="submit"
                  disabled={promoteSending || unlinkedChildren.length === 0}
                  className="bg-[#1F9FA4] hover:bg-[#178085] text-white rounded-sm"
                  data-testid="promote-submit-button"
                >
                  {promoteSending ? "Creating invite…" : "Create invite"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editPermsFor} onOpenChange={(v) => !v && closeEditPerms()}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3] max-w-lg" data-testid="mediator-edit-perms-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit permissions</DialogTitle>
            <DialogDescription className="text-[#6F828A]">
              Changes apply immediately. {editPermsFor?.used_by_name ? `${editPermsFor.used_by_name} will see the new scope on their next action.` : "The next person to use this code will get the updated permissions."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">Core</div>
              <div className="bg-white border border-[#D9E1E3] rounded-sm px-4">
                {CORE_PERMS.map((p) => (
                  <PermRow
                    key={p.key}
                    perm={p}
                    value={editPerms[p.key]}
                    onChange={(v) => setEditPerms({ ...editPerms, [p.key]: v })}
                    testid={`edit-perm-${p.key}`}
                  />
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">Optional — sensitive</div>
              <div className="bg-white border border-[#E8B8B2]/60 rounded-sm px-4">
                {OPTIONAL_PERMS.map((p) => (
                  <PermRow
                    key={p.key}
                    perm={p}
                    value={editPerms[p.key]}
                    onChange={(v) => setEditPerms({ ...editPerms, [p.key]: v })}
                    testid={`edit-perm-${p.key}`}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeEditPerms} data-testid="mediator-edit-perms-cancel">Cancel</Button>
            <Button onClick={savePerms} className="bg-[#1F9FA4] hover:bg-[#178085]" data-testid="mediator-edit-perms-save">Save permissions</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v77.48 — Edit per-child scope dialog. Mirrors the
          permissions dialog so the UX is consistent. */}
      <Dialog open={!!editScopeFor} onOpenChange={(v) => !v && closeEditScope()}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3] max-w-md" data-testid="mediator-edit-scope-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit children</DialogTitle>
            <DialogDescription className="text-[#6F828A]">
              Pick the children this third party can see. Changes apply on their next action. You can only grant access to children you manage or co-parent on.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-white border border-[#D9E1E3] rounded-sm p-3 max-h-[50vh] overflow-y-auto">
            {mediatorChildren.length === 0 ? (
              <div className="text-xs text-[#6F828A] italic">No children available to share.</div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-2 pb-2 border-b border-[#D9E1E3]">
                  <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">
                    {editScopeIds.length} of {mediatorChildren.length} selected
                  </span>
                  <div className="flex gap-2">
                    <button type="button" className="text-xs text-[#116B6E] hover:underline" onClick={() => setEditScopeIds(mediatorChildren.map((c) => c.id))} data-testid="edit-scope-select-all">
                      Select all
                    </button>
                    <span className="text-[#D9E1E3]">·</span>
                    <button type="button" className="text-xs text-[#825A41] hover:underline" onClick={() => setEditScopeIds([])} data-testid="edit-scope-clear">
                      Clear
                    </button>
                  </div>
                </div>
                <div className="space-y-1">
                  {mediatorChildren.map((c) => {
                    const checked = editScopeIds.includes(c.id);
                    return (
                      <label key={c.id} className="flex items-center gap-2.5 py-1 cursor-pointer" data-testid={`edit-scope-row-${c.id}`}>
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => {
                            setEditScopeIds((prev) => v
                              ? [...prev, c.id]
                              : prev.filter((x) => x !== c.id));
                          }}
                          data-testid={`edit-scope-checkbox-${c.id}`}
                        />
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color || "#1F9FA4" }} />
                        <span className="text-sm text-[#2E3A3F]">{c.name}</span>
                      </label>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeEditScope} data-testid="mediator-edit-scope-cancel">Cancel</Button>
            <Button onClick={saveScope} className="bg-[#1F9FA4] hover:bg-[#178085]" data-testid="mediator-edit-scope-save">Save children</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v77.49 — Third-party activity log dialog. Read-only,
          chronological list of resources the solicitor/mediator
          viewed via this specific invite. */}
      <Dialog open={!!viewsFor} onOpenChange={(v) => !v && closeViews()}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3] max-w-lg" data-testid="mediator-views-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Activity log</DialogTitle>
            <DialogDescription className="text-[#6F828A]">
              What this third party has actually viewed via this invite. Useful for solicitor / court-attestation purposes — “my solicitor reviewed the records on …”.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto" data-testid="mediator-views-list">
            {viewsLoading && (
              <div className="text-sm text-[#6F828A] py-6 text-center">Loading activity…</div>
            )}
            {!viewsLoading && views.length === 0 && (
              <div className="bg-[#FBF0DC] border border-[#E8C29A] rounded-sm p-4 text-sm text-[#5C3A1E]">
                No activity recorded yet. Once they open the messages, calendar, court bundle, journal, expenses or documents pages, you&apos;ll see entries here.
              </div>
            )}
            {!viewsLoading && views.length > 0 && (
              <ul className="divide-y divide-[#D9E1E3] border border-[#D9E1E3] rounded-sm">
                {views.map((v) => (
                  <li key={v.id} className="px-3 py-2.5" data-testid={`mediator-view-${v.id}`}>
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <span className="font-medium text-[#2E3A3F] text-sm">
                        {v.resource_label || v.resource_type}
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.06em] text-[#6F828A] font-mono-data flex-shrink-0">
                        {v.resource_type.replace("_", " ")}
                      </span>
                    </div>
                    <div className="text-[11px] text-[#6F828A] mt-0.5 font-mono-data">
                      {formatDT(v.viewed_at)}
                      {v.ip && <> · {v.ip}</>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeViews} className="border-[#D9E1E3] rounded-sm" data-testid="mediator-views-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <section className="border border-[#D9E1E3] bg-white rounded-md p-6" data-testid="bundle-section">
        <h2 className="font-heading text-lg font-medium">Records bundle export</h2>
        <p className="text-sm text-[#6F828A] mt-1">A court-ready PDF pack with messages, calendar, expenses and shared journal. Share with a mediator or solicitor.</p>
        <div className="mt-4 flex items-end gap-2 flex-wrap">
          <div>
            <Label>Last N days</Label>
            <Input type="number" min={1} max={730} value={bundleDays} onChange={(e) => setBundleDays(parseInt(e.target.value || "30", 10))} className="w-[140px]" data-testid="bundle-days-input" />
          </div>
          <Button onClick={exportBundle} className="bg-[#1F9FA4] hover:bg-[#178085]" data-testid="bundle-export-button"><Download className="w-4 h-4 mr-2" /> Export bundle PDF</Button>
        </div>
      </section>

      <section className="border border-[#D9E1E3] bg-white rounded-md p-6" data-testid="audit-section">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h2 className="font-heading text-lg font-medium">Activity log</h2>
            <p className="text-sm text-[#6F828A] mt-1">A 5-row preview of recent changes. Scroll within the card, or open the full log for filters and child-scoped views.</p>
          </div>
          <Link
            to="/app/activity"
            className="inline-flex items-center gap-1.5 text-sm text-[#1F9FA4] hover:text-[#178085] font-medium flex-shrink-0"
            data-testid="settings-view-full-activity-link"
          >
            View full activity log <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
          </Link>
        </div>
        <div className="mt-4 border border-[#EEF3F4] rounded-sm">
          <ul className="divide-y divide-[#EEF3F4]">
            {audit.length === 0 && <li className="px-3 py-4 text-sm text-[#6F828A]">No activity yet.</li>}
            {audit.slice(0, 5).map((a) => (
              <li key={a.id} className="px-3 py-2.5 flex items-center justify-between text-sm">
                <div>
                  <span className="font-mono-data text-[#2E3A3F]">{a.action}</span>
                  <span className="text-[#6F828A]"> · {a.entity}</span>
                </div>
                <div className="text-xs text-[#6F828A] font-mono-data">{formatDT(a.created_at)}</div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* v77.20 — Appearance preference. User-level setting (light/dark/system).
          v77.33 — Reordered to sit after Activity log so preferences come
          after core account & activity sections. */}
      <AppearanceCard />

      {/* v77.99.99 — Mobile bottom-nav customisation. Lets users pin
          their three favourite modules into the middle slots. */}
      <MobileNavCard />

      {/* Billing history — appears only if the user has Stripe invoices.
           Kept low on the page since paid users rarely need to dig through it. */}
      <BillingHistoryCard />

      {/* ===== Data export — GDPR Article 20 (Portability) ===== */}
      <section className="border border-[#D9E1E3] bg-white rounded-md p-6 md:p-7" data-testid="data-export-section">
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 rounded-sm bg-[#EAF6F7] border border-[#BFE3E5] text-[#1F9FA4] inline-flex items-center justify-center flex-shrink-0 mt-0.5">
            <Download className="w-4 h-4" strokeWidth={2} />
          </span>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#6F828A] font-semibold">Privacy &amp; data</div>
            <h2 className="font-heading text-lg font-semibold mt-1 text-[#273131]">Export all my data</h2>
            <p className="text-sm text-[#2E3A3F]/80 mt-2 max-w-xl leading-relaxed">
              Download a complete JSON copy of every record we hold about you — profile, household, children, expenses, messages, calendar events, journal entries, documents, payment credits, audit log and email history.
            </p>
            <p className="text-xs text-[#6F828A] mt-2 max-w-xl leading-relaxed">
              The bundle is structured for human reading and machine re-import. Credentials (password hashes, 2FA secrets, raw Stripe identifiers) are stripped before export.
            </p>
          </div>
        </div>
        <div className="mt-5">
          <Button
            type="button"
            onClick={async () => {
              const t = toast.loading("Preparing your data export…");
              try {
                const res = await fetch(`${API}/auth/export-all-my-data`, {
                  credentials: "include",
                });
                if (!res.ok) {
                  let detail = `Server returned ${res.status}`;
                  try { const j = await res.json(); detail = j.detail || detail; } catch (e) { void e; }
                  throw new Error(detail);
                }
                const blob = await res.blob();
                const cd = res.headers.get("content-disposition") || "";
                const m = cd.match(/filename="?([^";]+)"?/i);
                const filename = m ? m[1] : `coparentconsole-data-export-${new Date().toISOString().slice(0,10)}.json`;
                const url = URL.createObjectURL(new Blob([blob], { type: "application/json" }));
                const a = document.createElement("a");
                a.href = url; a.download = filename; a.style.display = "none";
                document.body.appendChild(a); a.click();
                setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 1000);
                toast.success("Your data export is downloading", { id: t });
              } catch (err) {
                toast.error(err?.message || "Could not generate export", { id: t });
              }
            }}
            variant="outline"
            className="rounded-sm border-[#BFE3E5] text-[#1F9FA4] hover:bg-[#EAF6F7]"
            data-testid="data-export-download-btn"
          >
            <Download className="w-4 h-4 mr-2" /> Download my data
          </Button>
        </div>
      </section>

      {/* ===== DANGER ZONE — kept at the very bottom, visually separated ===== */}
      <div className="pt-12 mt-8 border-t border-[#D9E1E3]" data-testid="account-data-divider" aria-hidden="true"></div>

      <section className="border-2 border-[#E8B8B2] bg-[#FDF2F0] rounded-md p-7 md:p-8" data-testid="account-data-section">
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 rounded-sm bg-[#FBEFED] border border-[#E8B8B2] text-[#8C2A22] inline-flex items-center justify-center flex-shrink-0 mt-0.5">
            <AlertTriangle className="w-4 h-4" strokeWidth={2} />
          </span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#8C2A22] font-semibold">Account &amp; Data</div>
            <h2 className="font-heading text-lg font-semibold mt-1 text-[#8C2A22]">Delete your account and data</h2>
            <p className="text-sm text-[#2E3A3F]/80 mt-2 max-w-xl leading-relaxed">
              You have the right to remove your account and all associated data at any time. This permanently deletes messages, calendar entries, expenses, documents, journal entries and child profiles. You&apos;ll be asked to confirm this several times before anything happens.
            </p>
            <p className="text-sm text-[#8C2A22] font-semibold mt-2">This action cannot be undone.</p>
          </div>
        </div>
        <div className="mt-5">
          <Button
            type="button"
            onClick={() => setDelOpen(true)}
            className="bg-[#C0291D] hover:bg-[#A8251A] active:bg-[#8C2A22] text-white rounded-sm shadow-sm transition-colors"
            data-testid="delete-account-open"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete account and data
          </Button>
        </div>
      </section>

      {/* ===== Feature request — visible but calm, sits below Delete account ===== */}
      <section className="border border-[#D9E1E3] bg-white rounded-md p-6" data-testid="feature-request-section">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <span className="w-8 h-8 rounded-sm bg-[#FDF4E2] text-[#8F6B1F] inline-flex items-center justify-center flex-shrink-0 mt-0.5">
              <Lightbulb className="w-4 h-4" strokeWidth={1.5} />
            </span>
            <div className="min-w-0">
              <h2 className="font-heading text-lg font-medium">Have an idea?</h2>
              <p className="text-sm text-[#6F828A] mt-1 max-w-xl">
                Tell us what would make CoParent Console better for your family. Every request is read as we plan new features.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {frMine.length > 0 || frShowMine ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={toggleMyRequests}
                className="text-[#6F828A] hover:text-[#2E3A3F] hover:bg-[#F4F7F7] rounded-sm"
                data-testid="feature-request-mine-toggle"
              >
                {frShowMine ? "Hide my requests" : `My requests${frMine.length ? ` (${frMine.length})` : ""}`}
              </Button>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={toggleMyRequests}
                className="text-[#6F828A] hover:text-[#2E3A3F] hover:bg-[#F4F7F7] rounded-sm"
                data-testid="feature-request-mine-toggle"
              >
                My requests
              </Button>
            )}
            <Button
              type="button"
              onClick={openFeatureRequest}
              variant="outline"
              className="border-[#1F9FA4] text-[#116B6E] hover:bg-[#E0F2F2] rounded-sm"
              data-testid="feature-request-open"
            >
              <Lightbulb className="w-4 h-4 mr-2" /> Request a feature
            </Button>
          </div>
        </div>
        {frShowMine && (
          <div className="mt-4" data-testid="feature-request-mine-list">
            {frMine.length === 0 ? (
              <div className="text-sm text-[#6F828A] border border-dashed border-[#D9E1E3] rounded-sm p-4 text-center" data-testid="feature-request-mine-empty">
                You haven&apos;t submitted any requests yet.
              </div>
            ) : (
              <ul className="divide-y divide-[#D9E1E3] border border-[#D9E1E3] rounded-md max-h-[260px] overflow-y-auto">
                {frMine.map((r) => (
                  <li key={r.id} className="p-3 text-sm" data-testid={`feature-request-mine-row-${r.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="font-medium text-[#2E3A3F]">{r.title}</div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {r.category && (
                          <span className="text-[10px] uppercase tracking-[0.08em] px-2 py-0.5 bg-[#F4F7F7] text-[#6F828A] rounded-sm">{r.category}</span>
                        )}
                        {r.priority && (
                          <span className="text-[10px] uppercase tracking-[0.08em] px-2 py-0.5 bg-[#FDF4E2] text-[#8F6B1F] rounded-sm">{r.priority.replace(/_/g, " ")}</span>
                        )}
                      </div>
                    </div>
                    {r.description && (
                      <div className="text-xs text-[#6F828A] mt-1 whitespace-pre-wrap">{r.description}</div>
                    )}
                    <div className="text-[11px] text-[#6F828A] font-mono-data mt-1.5">Submitted {formatDT(r.created_at)}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      <Dialog open={frOpen} onOpenChange={(v) => !v && closeFeatureRequest()}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3] max-w-lg" data-testid="feature-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Request a feature</DialogTitle>
            <DialogDescription className="text-[#2E3A3F]">
              Tell us what you&apos;d like to see. Keep it short — we&apos;ll follow up if we need more detail.
            </DialogDescription>
          </DialogHeader>
          {frSubmitted ? (
            <div className="py-2 text-center space-y-3" data-testid="feature-request-success">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#E0F2F2] text-[#116B6E]">
                <CheckCircle2 className="w-6 h-6" strokeWidth={1.5} />
              </div>
              <div className="font-heading text-lg text-[#2E3A3F]">Thanks for your feedback</div>
              <p className="text-sm text-[#6F828A] max-w-sm mx-auto">
                We review all feature requests as we improve the app.
              </p>
              <div className="flex justify-center pt-1">
                <Button type="button" onClick={closeFeatureRequest} className="bg-[#1F9FA4] hover:bg-[#198185] text-white rounded-sm" data-testid="feature-request-done">Done</Button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-4 text-sm">
                <div>
                  <Label htmlFor="fr-title">Feature title</Label>
                  <Input
                    id="fr-title"
                    value={frTitle}
                    onChange={(e) => { setFrTitle(e.target.value); setFrError(""); }}
                    placeholder="E.g. Shared grocery list"
                    maxLength={120}
                    data-testid="feature-request-title"
                  />
                </div>
                <div>
                  <Label htmlFor="fr-desc">Description</Label>
                  <Textarea
                    id="fr-desc"
                    value={frDesc}
                    onChange={(e) => { setFrDesc(e.target.value); setFrError(""); }}
                    placeholder="What should it do? When would you use it?"
                    rows={4}
                    maxLength={4000}
                    className="resize-y"
                    data-testid="feature-request-description"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="fr-cat">Category <span className="text-[#6F828A]">(optional)</span></Label>
                    <Select value={frCategory || undefined} onValueChange={(v) => setFrCategory(v === "__none__" ? "" : v)}>
                      <SelectTrigger id="fr-cat" data-testid="feature-request-category">
                        <SelectValue placeholder="Choose area" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No category</SelectItem>
                        <SelectItem value="messaging">Messaging</SelectItem>
                        <SelectItem value="expenses">Expenses</SelectItem>
                        <SelectItem value="calendar">Calendar</SelectItem>
                        <SelectItem value="children">Children profiles</SelectItem>
                        <SelectItem value="settings">Settings</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="fr-pri">Priority <span className="text-[#6F828A]">(optional)</span></Label>
                    <Select value={frPriority || undefined} onValueChange={(v) => setFrPriority(v === "__none__" ? "" : v)}>
                      <SelectTrigger id="fr-pri" data-testid="feature-request-priority">
                        <SelectValue placeholder="How important is it?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Not sure</SelectItem>
                        <SelectItem value="nice_to_have">Nice to have</SelectItem>
                        <SelectItem value="important">Important</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {frError && (
                  <div className="text-sm text-[#8C2A22] bg-[#FBEFED] border border-[#E8B8B2] rounded-sm px-3 py-2" data-testid="feature-request-error">
                    {frError}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={closeFeatureRequest} disabled={frBusy} data-testid="feature-request-cancel">Cancel</Button>
                <Button
                  type="button"
                  onClick={submitFeatureRequest}
                  disabled={frBusy || !frTitle.trim() || !frDesc.trim()}
                  className="bg-[#1F9FA4] hover:bg-[#198185] text-white rounded-sm"
                  data-testid="feature-request-submit"
                >
                  {frBusy ? "Sending…" : "Submit"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={unlinkOpen} onOpenChange={(v) => !v && setUnlinkOpen(false)}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3] max-w-lg" data-testid="unlink-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Unlink your co-parent</DialogTitle>
            <DialogDescription className="text-[#2E3A3F]">
              You&apos;ll be moved to a new empty account. Your co-parent keeps the shared records (messages, expenses, children, journals and documents) on their side. <span className="font-medium">If you unlink, you&apos;ll need to go through the linking process again to reconnect.</span>
            </DialogDescription>
          </DialogHeader>
          {!unlinkPreflight && !unlinkError && (
            <div className="text-sm text-[#6F828A]">Checking your account…</div>
          )}
          {unlinkPreflight && (
            <div className="space-y-4">
              {unlinkPreflight.outstanding_count > 0 && (
                <div className="border border-[#E8C29A] bg-[#FBF0DC] text-[#6F4E1E] rounded-sm p-3 text-sm space-y-2" data-testid="unlink-outstanding-warning">
                  <div className="font-medium">There are outstanding balances on this account.</div>
                  <div>{unlinkPreflight.outstanding_count} expense{unlinkPreflight.outstanding_count === 1 ? "" : "s"} with <span className="font-mono-data">{formatMoney(unlinkPreflight.outstanding_total, unlinkPreflight.currency)}</span> unsettled. If you unlink, you will lose access to this shared history. Please export your records before continuing.</div>
                  <Button variant="outline" onClick={() => downloadFile(`${API}/export/bundle/pdf?days=3650`, `coparent-bundle-all-${new Date().toISOString().slice(0,10)}.pdf`, { label: "Generating full bundle", successMessage: "Bundle ready" })} className="border-[#B88A3A] text-[#6F4E1E] hover:bg-[#F5E7C9] rounded-sm" size="sm" data-testid="unlink-export-button">
                    Export records bundle (PDF with receipts)
                  </Button>
                </div>
              )}
              <ul className="text-xs text-[#6F828A] grid grid-cols-2 gap-x-4 gap-y-1.5">
                <li>Messages on this account: <span className="font-mono-data text-[#2E3A3F]">{unlinkPreflight.total_messages}</span></li>
                <li>Children profiles: <span className="font-mono-data text-[#2E3A3F]">{unlinkPreflight.total_children}</span></li>
                <li>Calendar events: <span className="font-mono-data text-[#2E3A3F]">{unlinkPreflight.total_events}</span></li>
                <li>Journal entries: <span className="font-mono-data text-[#2E3A3F]">{unlinkPreflight.total_journals}</span></li>
              </ul>
              <label className="flex items-start gap-2 text-sm text-[#2E3A3F] cursor-pointer select-none" data-testid="unlink-ack-label">
                <input type="checkbox" checked={unlinkAck} onChange={(e) => setUnlinkAck(e.target.checked)} className="w-4 h-4 rounded-sm border-[#D9E1E3] accent-[#C0291D] mt-0.5" data-testid="unlink-ack-input" />
                <span>I understand I will lose access to this shared history and can only reconnect via a fresh invite code.</span>
              </label>
              {unlinkPreflight.coparent && (
                <div>
                  <Label className="text-xs text-[#6F828A]">Type your co-parent&apos;s name to confirm: <span className="font-medium text-[#2E3A3F]">{unlinkPreflight.coparent.name}</span></Label>
                  <Input value={unlinkConfirmName} onChange={(e) => setUnlinkConfirmName(e.target.value)} placeholder={unlinkPreflight.coparent.name} data-testid="unlink-confirm-name-input" />
                </div>
              )}
              {unlinkError && <div className="text-sm text-[#8C2A22] border border-[#E8B8B2] bg-[#FBEFED] rounded-sm p-2" data-testid="unlink-error">{unlinkError}</div>}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUnlinkOpen(false)} disabled={unlinkBusy} data-testid="unlink-cancel-button">Cancel</Button>
            <Button
              onClick={doUnlink}
              disabled={unlinkBusy || !unlinkAck || (unlinkPreflight?.coparent && unlinkConfirmName.trim().toLowerCase() !== (unlinkPreflight.coparent.name || "").trim().toLowerCase())}
              className="bg-[#C0291D] hover:bg-[#9A1F16] text-white rounded-sm"
              data-testid="unlink-confirm-button"
            >
              {unlinkBusy ? "Unlinking…" : "Unlink co-parent"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={delOpen} onOpenChange={(v) => !v && closeDelete()}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3] max-w-md" data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#8C2A22]">Delete your account</DialogTitle>
            <DialogDescription className="text-[#2E3A3F]">
              This will permanently delete your account and all associated data — messages, calendar entries, expenses, documents, journal entries and child profiles. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="delete-account-ack-row">
              <input
                type="checkbox"
                checked={delAck}
                onChange={(e) => { setDelAck(e.target.checked); setDelError(""); }}
                className="mt-0.5 accent-[#8C2A22]"
                data-testid="delete-account-ack"
              />
              <span>I understand this action is permanent and cannot be undone.</span>
            </label>
            <div>
              <Label htmlFor="del-pw">Re-enter your password</Label>
              <PasswordInput
                id="del-pw"
                value={delPassword}
                onChange={(e) => { setDelPassword(e.target.value); setDelError(""); }}
                autoComplete="current-password"
                data-testid="delete-account-password"
              />
            </div>
            <div>
              <Label htmlFor="del-phrase">Type <span className="font-mono-data font-semibold tracking-wider">DELETE</span> to confirm</Label>
              <Input
                id="del-phrase"
                value={delPhrase}
                onChange={(e) => { setDelPhrase(e.target.value); setDelError(""); }}
                placeholder="DELETE"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                data-testid="delete-account-phrase"
              />
            </div>
            {delError && (
              <div className="text-sm text-[#8C2A22] bg-[#FBEFED] border border-[#E8B8B2] rounded-sm px-3 py-2" data-testid="delete-account-error">
                {delError}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={closeDelete} disabled={delBusy} data-testid="delete-account-cancel">Cancel</Button>
            <Button
              type="button"
              onClick={submitDeleteAccount}
              disabled={delBusy || !delAck || !delPassword || delPhrase !== "DELETE"}
              className="bg-[#8C2A22] hover:bg-[#6E1F19] text-white rounded-sm"
              data-testid="delete-account-confirm"
            >
              {delBusy ? "Deleting…" : "Delete my account permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3]">
          <DialogHeader><DialogTitle className="font-heading">Disable 2FA</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6F828A]">Enter a current authenticator code to confirm.</p>
          <Input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} inputMode="numeric" maxLength={6} data-testid="twofa-disable-input" />
          <DialogFooter><Button onClick={disable2fa} className="bg-[#825A41] hover:bg-[#6d4935] text-white" data-testid="twofa-disable-confirm">Disable</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={regenOpen} onOpenChange={(o) => { if (!regenBusy) setRegenOpen(o); }}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3]">
          <DialogHeader><DialogTitle className="font-heading">Regenerate recovery codes</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6F828A]">
            Enter a current authenticator code to confirm. This will invalidate your previous recovery codes.
          </p>
          <Input
            value={regenCode}
            onChange={(e) => setRegenCode(e.target.value)}
            inputMode="numeric"
            maxLength={6}
            data-testid="twofa-regen-codes-input"
          />
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setRegenOpen(false)}
              disabled={regenBusy}
              data-testid="twofa-regen-codes-cancel"
            >Cancel</Button>
            <Button
              onClick={regenerateRecoveryCodes}
              disabled={regenBusy}
              className="bg-[#1F9FA4] hover:bg-[#178085] text-white"
              data-testid="twofa-regen-codes-confirm"
            >{regenBusy ? "Working…" : "Regenerate"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v77.41 — Subtle Irish ownership line. Bottom of Settings page,
          below every action including Delete account. Muted, low
          contrast, not a banner. */}
      <p
        className="text-xs text-[#A3B0B5] text-center pt-4 pb-2"
        data-testid="settings-irish-ownership-line"
      >
        Proudly Irish-made and Irish-owned. Built with privacy, trust, and families in mind.
      </p>
    </div>
  );
}

function PrefRow({ label, sub, value, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 border border-[#D9E1E3] rounded-sm p-3" data-testid={testid}>
      <div className="min-w-0">
        <div className="text-sm font-medium text-[#2E3A3F]">{label}</div>
        <div className="text-xs text-[#6F828A] mt-0.5">{sub}</div>
      </div>
      <Switch checked={value} onCheckedChange={onChange} data-testid={`${testid}-switch`} />
    </div>
  );
}

// v77.99.140 (iter 124.monthly-summary) — Notification toggle for the
// end-of-month statement email. Uses its own endpoint (not the
// generic prefs bucket) because the emails are sent by a scheduler
// that reads `users.monthly_summary_emails` directly.
function MonthlySummaryToggle() {
  const [enabled, setEnabled] = useState(null); // null → loading
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/settings/monthly-summary-emails");
        setEnabled(!!r.data?.enabled);
      } catch (_) {
        setEnabled(true); // default ON if the endpoint is unavailable
      }
    })();
  }, []);

  const onToggle = async (v) => {
    if (busy || enabled === null) return;
    setBusy(true);
    // Optimistic — snap the switch immediately.
    setEnabled(v);
    try {
      await api.put("/settings/monthly-summary-emails", { enabled: !!v });
      toast.success(v ? "Monthly summary emails on" : "Monthly summary emails off");
    } catch (e) {
      // Revert on failure.
      setEnabled(!v);
      toast.error(e?.response?.data?.detail || "Could not update preference.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mt-5 border-t border-[#D9E1E3] pt-4 flex items-start justify-between gap-3 flex-wrap"
      data-testid="monthly-summary-toggle-row"
    >
      <div className="min-w-0 max-w-xl">
        <div className="text-sm font-medium text-[#2E3A3F]">Monthly expense summary emails</div>
        <p className="text-xs text-[#6F828A] mt-1 leading-relaxed">
          On the last day of each month you&apos;ll receive a clean end-of-month statement:
          totals logged, received, settled and outstanding — plus a &ldquo;Needs your attention&rdquo;
          section and both the Monthly Expenses PDF and Reconciliation snapshot PDF attached.
          Suitable for solicitors, mediators or the Court as a monthly financial record.
        </p>
      </div>
      <Switch
        checked={!!enabled}
        onCheckedChange={onToggle}
        disabled={enabled === null || busy}
        data-testid="monthly-summary-toggle-switch"
      />
    </div>
  );
}



// v72 — Co-parent access overview. Renders a clean per-child × co-parent
// table with revoke per child + a list of pending invites (so the
// primary parent can see which links haven't been consumed yet).
function CoparentAccessSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get("/household/coparent-access");
      setData(payload);
    } catch (err) {
      // Non-primary parents get 403 — silently swallow.
      if (err.response?.status !== 403) {
        toast.error(err.response?.data?.detail || "Couldn't load co-parent access");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const revokeAccess = async (childId, userId, name) => {
    if (!window.confirm(`Revoke ${name}'s access to this child? They'll keep their account but stop seeing this child's data. This action is logged.`)) return;
    setBusy(true);
    try {
      await api.post(`/children/${childId}/coparents/revoke`, { user_id: userId });
      toast.success("Access revoked");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't revoke access");
    } finally {
      setBusy(false);
    }
  };

  const revokeInvite = async (childId, inviteId) => {
    if (!window.confirm("Revoke this pending invite link? Anyone holding the link won't be able to use it anymore.")) return;
    setBusy(true);
    try {
      await api.post(`/children/${childId}/invites/${inviteId}/revoke`);
      toast.success("Invite revoked");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't revoke invite");
    } finally {
      setBusy(false);
    }
  };

  // v76.4 — One-shot bulk revoke. Removes the named co-parent from every
  // child they currently have access to. Soft-revokes all rows (audit
  // trail intact). Confirmation step is mandatory because this is a big
  // privacy change.
  const revokeAll = async (cp) => {
    const name = cp.name || cp.email;
    if (cp.child_ids.length === 0) return;
    if (!window.confirm(`Revoke ALL access for ${name}? They'll keep their account but stop seeing every child's data. This action is logged.`)) return;
    setBusy(true);
    try {
      const { data: result } = await api.post(`/household/coparents/${cp.id}/revoke-all`);
      toast.success(`Access revoked for ${name} on ${result.revoked_count} child${result.revoked_count === 1 ? "" : "ren"}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't revoke all access");
    } finally {
      setBusy(false);
    }
  };

  // v76.4 — Re-send a pending invite. Token doesn't change — any link
  // already shared still works — we just retrigger the email and bump
  // last_resent_at. Useful when the invitee lost the email or never saw
  // it land. If no email was attached to the invite, the API returns the
  // join URL again so the inviter can copy it.
  const resendInvite = async (childId, inviteId) => {
    setBusy(true);
    try {
      const { data: result } = await api.post(`/children/${childId}/invites/${inviteId}/resend`);
      if (result.emailed) {
        toast.success("Invite email resent");
      } else if (result.join_url) {
        try { await navigator.clipboard.writeText(result.join_url); } catch (e) { console.debug("[settings] clipboard write failed:", e?.message); }
        toast.success("Invite link copied to clipboard");
      } else {
        toast.success("Invite refreshed");
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't resend invite");
    } finally {
      setBusy(false);
    }
  };

  // v76.4 — Inline "+ Add child" affordance. Grants the named co-parent
  // access to one more child without leaving the Settings page. Backed
  // by POST /household/coparents/{uid}/grant which is idempotent.
  const grantChild = async (cp, childId) => {
    setBusy(true);
    try {
      await api.post(`/household/coparents/${cp.id}/grant`, { child_id: childId });
      const c = (data?.children || []).find((x) => x.id === childId);
      toast.success(`${cp.name || cp.email} now has access to ${c?.name || "this child"}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't grant access");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;
  if (!data) return null;
  const childById = Object.fromEntries((data.children || []).map((c) => [c.id, c]));

  return (
    <section className="border border-[#D9E1E3] bg-white rounded-md p-6" data-testid="coparent-access-section">
      <h2 className="font-heading text-lg font-medium">Co-parent access</h2>
      <p className="text-sm text-[#6F828A] mt-1">
        See which co-parent has access to which child. Each co-parent only sees information for the children listed beside their name.
      </p>

      {data.coparents.length === 0 && (
        <div className="mt-4 text-sm text-[#6F828A] italic" data-testid="coparent-access-empty">
          No co-parents yet. Open a child profile and tap <span className="font-medium">Invite co-parent</span> to send a child-scoped invite link.
        </div>
      )}

      {data.coparents.length > 0 && (
        <ul className="mt-4 space-y-3" data-testid="coparent-access-list">
          {data.coparents.map((cp) => (
            <li key={cp.id} className="border border-[#D9E1E3] rounded-sm p-3" data-testid={`coparent-row-${cp.id}`}>
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <div>
                  <div className="text-sm font-medium text-[#2E3A3F]">{cp.name || cp.email}</div>
                  <div className="text-xs text-[#6F828A] font-mono-data">{cp.email}</div>
                </div>
                {cp.child_ids.length === 0 ? (
                  <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] bg-[#EEF3F4] border border-[#D9E1E3] rounded-sm px-1.5 py-0.5">
                    No active access
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => revokeAll(cp)}
                    disabled={busy}
                    className="text-[11px] text-[#8C2A36] hover:bg-[#FDF5F6] border border-[#E6CDD0] rounded-sm px-2 py-0.5 inline-flex items-center gap-1"
                    title="Revoke this co-parent's access to every child in one step"
                    data-testid={`coparent-revoke-all-${cp.id}`}
                  >
                    Revoke all access
                  </button>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`coparent-chips-${cp.id}`}>
                {cp.child_ids.map((cid) => {
                  const c = childById[cid];
                  if (!c) return null;
                  return (
                    <span
                      key={cid}
                      className="inline-flex items-center gap-1.5 text-xs border border-[#D9E1E3] bg-[#FAFBFB] rounded-sm pl-1.5 pr-1 py-0.5"
                      data-testid={`coparent-child-chip-${cp.id}-${cid}`}
                    >
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color || "#1F9FA4" }} />
                      {c.name}
                      {c.archived_at && <span className="text-[9px] uppercase text-[#6F828A]">archived</span>}
                      <button
                        type="button"
                        onClick={() => revokeAccess(cid, cp.id, cp.name || cp.email)}
                        disabled={busy}
                        className="ml-1 w-4 h-4 inline-flex items-center justify-center rounded-sm text-[#8C2A36] hover:bg-[#FDF5F6]"
                        title="Revoke access to this child"
                        data-testid={`coparent-revoke-${cp.id}-${cid}`}
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
                {/* v76.4 — Inline "+ Add child" picker. Lists every child
                    in the household this co-parent doesn't already have
                    access to. One click grants — idempotent on backend. */}
                {(() => {
                  const granted = new Set(cp.child_ids);
                  const candidates = (data.children || []).filter((c) => !granted.has(c.id) && !c.archived_at);
                  if (candidates.length === 0) return null;
                  return (
                    <details className="text-xs" data-testid={`coparent-add-child-${cp.id}`}>
                      <summary className="cursor-pointer inline-flex items-center gap-1 border border-dashed border-[#A7D8DA] text-[#116B6E] rounded-sm px-1.5 py-0.5 hover:bg-[#E0F2F2]/40 select-none">
                        + Add child
                      </summary>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {candidates.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => grantChild(cp, c.id)}
                            disabled={busy}
                            className="inline-flex items-center gap-1.5 text-xs border border-[#D9E1E3] bg-white rounded-sm px-1.5 py-0.5 hover:border-[#1F9FA4] hover:bg-[#E0F2F2]/40"
                            data-testid={`coparent-grant-${cp.id}-${c.id}`}
                          >
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color || "#1F9FA4" }} />
                            {c.name}
                          </button>
                        ))}
                      </div>
                    </details>
                  );
                })()}
              </div>
            </li>
          ))}
        </ul>
      )}

      {data.pending_invites.length > 0 && (
        <div className="mt-5 border-t border-[#D9E1E3] pt-4" data-testid="pending-invites-block">
          <Label className="text-xs uppercase tracking-[0.1em] text-[#6F828A]">Pending invites</Label>
          <ul className="mt-2 space-y-2">
            {data.pending_invites.map((inv) => {
              const names = (inv.child_ids || []).map((cid) => childById[cid]?.name || "Child").join(", ");
              return (
                <li key={inv.id} className="flex items-center justify-between gap-2 border border-[#D9E1E3] rounded-sm p-2 text-sm" data-testid={`pending-invite-${inv.id}`}>
                  <div>
                    <div className="text-[#2E3A3F]">{names}</div>
                    {inv.email && <div className="text-xs text-[#6F828A] font-mono-data">{inv.email}</div>}
                    {inv.last_resent_at && (
                      <div className="text-[10px] text-[#6F828A] mt-0.5">
                        Last resent {new Date(inv.last_resent_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => resendInvite(inv.child_ids[0], inv.id)}
                      disabled={busy}
                      className="text-[#116B6E] hover:bg-[#E0F2F2]"
                      data-testid={`pending-invite-resend-${inv.id}`}
                    >
                      Resend
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => revokeInvite(inv.child_ids[0], inv.id)}
                      disabled={busy}
                      className="text-[#8C2A36] hover:bg-[#FDF5F6]"
                      data-testid={`pending-invite-revoke-${inv.id}`}
                    >
                      Revoke
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}


// v76 — My access summary. Renders for non-primary parents in the
// household. Shows the children this user can see, the primary parent
// who manages access, and a calm note for the zero-access case. Reads
// /api/household/my-access (any household member can call it; the
// payload is scoped to the caller).
function MyAccessSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data: payload } = await api.get("/household/my-access");
        if (alive) setData(payload);
      } catch (err) {
        // Calm fail — non-blocking. Don't toast on logout race or
        // if `/household/my-access` is racing with sign-out. Logged at
        // debug level so dev tools can still trace it without spamming
        // production consoles for the (frequent) logout-race case.
        console.debug("[settings/access] my-access fetch skipped:", err?.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading || !data) return null;
  // Hide for mediators and operators — this section is specifically the
  // co-parent trust-check view. (The primary-parent guard is upstream.)
  if (data.role !== "coparent") return null;

  const children = data.children || [];
  // v77.48 — Render the managing parents of CURRENTLY VISIBLE
  // children, not the household creator. In a blended family the
  // caller may have multiple managers (one per child); we list them
  // all. `data.managers` is the new per-child source; `data.primary`
  // remains for back-compat with older payloads.
  const managers = (data.managers && data.managers.length > 0)
    ? data.managers
    : (data.primary ? [data.primary] : []);
  const hasAccess = children.length > 0;

  return (
    <section className="border border-[#D9E1E3] bg-white rounded-md p-6" data-testid="my-access-section">
      <h2 className="font-heading text-lg font-medium inline-flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-[#1F9FA4]" /> My access
      </h2>
      <p className="text-sm text-[#6F828A] mt-1">
        A quick check of what you can see. Access is granted by the managing parent on a per-child basis — you only see information for the children listed here.
      </p>

      {!hasAccess && (
        <div
          className="mt-4 border border-[#E8C29A] bg-[#FBF0DC] text-[#5C3A1E] rounded-sm p-4 text-sm"
          data-testid="my-access-empty"
        >
          <div className="font-medium mb-1">No children have been assigned to you yet.</div>
          <div className="text-[#6F4E1E]">
            Please ask the managing parent of the child you should have access to, to invite you. They can do this from <span className="font-medium">Children → Invite co-parent</span>.
          </div>
        </div>
      )}

      {hasAccess && (
        <>
          <div className="mt-4 flex flex-wrap gap-1.5" data-testid="my-access-children-list">
            {children.map((c) => (
              <span
                key={c.id}
                className="inline-flex items-center gap-1.5 text-sm border border-[#D9E1E3] bg-[#FAFBFB] rounded-sm pl-2 pr-2 py-1"
                data-testid={`my-access-child-chip-${c.id}`}
              >
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color || "#1F9FA4" }} />
                <span className="font-medium text-[#2E3A3F]">{c.name}</span>
                {c.archived_at && (
                  <span className="text-[9px] uppercase tracking-[0.08em] text-[#6F828A]">archived</span>
                )}
              </span>
            ))}
          </div>
          {managers.length > 0 && (
            <div className="mt-3 text-xs text-[#6F828A]" data-testid="my-access-primary-line">
              Access managed by{" "}
              {managers.map((m, i) => (
                <span key={m.id || i}>
                  {i > 0 && (i === managers.length - 1 ? " and " : ", ")}
                  <span className="text-[#2E3A3F] font-medium">{m.name || "the managing parent"}</span>
                  {m.email && (
                    <> · <span className="font-mono-data">{m.email}</span></>
                  )}
                </span>
              ))}
              . If you need access to other children, please ask {managers.length === 1 ? "them" : "the relevant managing parent"} to invite you.
            </div>
          )}
        </>
      )}
    </section>
  );
}
